/**
 * bubu-cart.js — única capa sobre el carrito de Shopify.
 *
 * - Estado real en /cart/add.js, /cart/update.js, /cart.js. Nada en localStorage.
 * - Lo que se pinta es un modelo DERIVADO (último carrito del servidor + lo que has
 *   pulsado y aún no está confirmado), y lo que se manda es la diferencia entre ambos.
 *   Ver el comentario largo de BubuCart: es lo que hace que meter y quitar a toda prisa
 *   no descuadre nunca la barra de hitos.
 * - Repintado con el Section Rendering API: cada operación pide las secciones que
 *   dependen del carrito (bubu-caja, bubu-cesta, bubu-barra-movil) y copia su interior a
 *   todos los contenedores [data-<id>-cont] de la página. No se reconstruye nada a mano.
 * - Las animaciones de añadir viven aquí y solo aquí: BubuAnim.grande / BubuAnim.mas
 *   (las hereda cualquier botón con [data-bubu-add] o [data-bubu-mas]) y _vuela(), que
 *   manda la miniatura del producto a la barra de hitos y le da un golpe al llegar.
 * - Activador de regalo: en cuanto hay una línea normal, se añade la variante a 0 € con
 *   _regalo: activador (cantidad 1). Si solo quedan regalos, se vacía el carrito.
 * - Apertura automática: la hoja que toca (la caja en la tienda, el carrito fuera) se
 *   abre SOLO al pasar de 0 a 1 línea, 620 ms después del clic. A partir de ahí no se
 *   abre nada y el vuelo hace el trabajo, para no cortar la compra a cada artículo.
 *
 * API: BubuCart.add(items) · BubuCart.change(key, qty) · BubuCart.get() · BubuCart.abrirCesta()
 *      document.addEventListener('bubu:cart', e => e.detail.cart)
 */

const SECCIONES_POSIBLES = ['bubu-caja', 'bubu-cesta', 'bubu-barra-movil'];
/**
 * Solo se piden al servidor las secciones que existen en ESTA página. Pedir las tres
 * siempre obligaba a Shopify a renderizar dos que no se iban a usar en cada «Añadir».
 * Se recalcula en cada operación porque «Monta tu caja» entra y sale del DOM al cambiar
 * de anchura.
 */
function seccionesVivas() {
  const hay = SECCIONES_POSIBLES.filter((id) => document.querySelector(`[data-${id}-cont]`));
  return hay.length ? hay : SECCIONES_POSIBLES;
}
const money = (c) => (c / 100).toFixed(2).replace('.', ',') + ' €';
const CFG = leeConfig();

function leeConfig() {
  try {
    const el = document.getElementById('bubu-config');
    return el ? JSON.parse(el.textContent) : {};
  } catch (_) {
    return {};
  }
}
const esMovil = () => window.innerWidth < 820;

/**
 * Misma foto, mismo ancho, misma URL. El hueco optimista de la caja pintaba la foto de
 * la tarjeta (width=600) y, un instante después, el repintado del servidor la cambiaba
 * por la de width=200: otra URL, otra descarga y un parpadeo en medio. Reescribiendo el
 * ancho en la URL de Shopify las dos piden EXACTAMENTE el mismo archivo, así que la
 * segunda sale de caché y la foto no se mueve.
 */
function anchoImg(src, w) {
  if (!src) return src;
  try {
    const u = new URL(src, location.origin);
    if (!u.searchParams.has('width') && !u.searchParams.has('v')) return src;
    u.searchParams.set('width', String(w));
    return u.pathname + u.search === src ? src : u.toString();
  } catch (_) {
    return src;
  }
}
/** Se pide la foto en el instante del clic, antes de que el servidor conteste. */
const _precargadas = new Set();
function precarga(src) {
  if (!src || _precargadas.has(src)) return;
  _precargadas.add(src);
  const im = new Image();
  im.decoding = 'async';
  im.src = src;
}

/* ------------------------------------------------------------------
   SLIME GRATIS: el enlace del correo de bienvenida.

   El correo enlaza a bubuslimes.com/?regalo=CLAVE (la clave sale de los ajustes del
   tema). Al abrirlo, esto guarda el canje en una cookie durante N días y, desde ese
   momento, _regalosEnModelo mete el producto de regalo (a 0 €, con _regalo: bienvenida)
   en cuanto la caja tiene un Bubu. Entra en el mismo viaje que el activador: no hay
   petición aparte ni carrera.

   «Solo una vez», en tres cerraduras que no dependen unas de otras:
   1. Al confirmarse el pedido, el carrito de Shopify cambia de token. Aquí se apunta el
      token del carrito que llevaba el regalo; si al volver ya no es ese, el regalo se da
      por usado en este navegador (localStorage) y la cookie se borra. El enlace, en ese
      navegador, deja de regalar.
   2. Shopify Flow etiqueta al cliente del pedido con la etiqueta de los ajustes; un
      cliente identificado con ella recibe bienvenidaCanjeado=true desde Liquid y el
      enlace no hace nada, sea el navegador que sea.
   3. El producto de regalo lleva stock: sin unidades, bienvenidaGratis es false y no se
      añade nada (mejor sin regalo que con un carrito que falla).
   ------------------------------------------------------------------ */
const REGALO_COOKIE = 'bubu_regalo';
const REGALO_USADO = 'bubu_regalo_usado';
const REGALO_TOKEN = 'bubu_regalo_token';
function leeCookie(n) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : '';
}
function ponCookie(n, v, dias) {
  document.cookie = n + '=' + encodeURIComponent(v) + '; max-age=' + Math.round(dias * 86400) + '; path=/; SameSite=Lax; Secure';
}
function borraCookie(n) {
  document.cookie = n + '=; max-age=0; path=/; SameSite=Lax; Secure';
}
function guarda(k, v) {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch (_) { /* sin almacenamiento (modo privado) el regalo sigue entrando; solo pierde la cerradura 1 */ }
}
function lee(k) {
  try {
    return localStorage.getItem(k);
  } catch (_) {
    return null;
  }
}
/* ------------------------------------------------------------------
   REGALOS POR ENLACE (09/10/2026). La bienvenida dejó de ser la única: los correos de
   rango y de cumpleaños traen su propia clave (?regalo=mystery, leyenda, tarta). Cada
   clave es un «canje» con la MISMA mecánica que la bienvenida de siempre —cookie,
   cerradura del token y producto a 0 €—, solo cambia qué producto mete y qué pide la
   cesta para meterlo:
     · requiere 'caja': al menos un Bubu de pago (bienvenida, mystery, leyenda).
     · requiere 'pack': un cumple montado desde /pages/cumples (tarta).
   La bienvenida conserva sus nombres de cookie y de localStorage, así que a quien ya
   tenía el enlace abierto no le cambia nada.
   ------------------------------------------------------------------ */
class Canje {
  constructor(o) {
    Object.assign(this, o);
    this._pendiente = false;
    this._pidiendo = false;
  }
  /** ¿Hay un canje vigente en este navegador que todavía no se ha usado? */
  activa() {
    if (!this.id || !this.gratis || this.bloqueado) return false;
    if (this._pendiente) return false;
    if (lee(this.usado) === '1') return false;
    return leeCookie(this.cookie) === String(this.clave || '');
  }
  marcaUsado() {
    guarda(this.usado, '1');
    guarda(this.token, null);
    borraCookie(this.cookie);
  }
  /** La URL traía ESTA clave: guarda el canje (o avisa de que ya se usó). */
  desdeClave() {
    if (this.bloqueado || lee(this.usado) === '1') {
      avisoRegalo(this.avisoUsado || 'Este regalo ya se ha canjeado.');
      return;
    }
    if (!this.id) return;
    ponCookie(this.cookie, this.clave, Number(this.dias || 15));
    // Con un token apuntado, la cerradura 1 aún tiene que decir si el pedido ya se hizo
    // (vigila lo comprueba enseguida): ni se promete el regalo ni se niega hasta saberlo.
    if (lee(this.token)) return;
    avisoRegalo(this.aviso);
  }
  /**
   * Cerradura 1. Se llama cada vez que hay carrito confirmado por Shopify: si lleva el
   * regalo, se apunta su token; si el token apuntado ya no es el del carrito actual, el
   * pedido se hizo (o el carrito caducó) y el regalo queda usado.
   */
  vigila(base) {
    if (!base) return;
    const apuntado = lee(this.token);
    const tipo = this.tipo;
    const lleva = (base.items || []).some((i) => i.properties && i.properties._regalo === tipo);
    /* El carrito que viaja dentro del HTML (cart | json de Liquid) NO trae el token; solo
       /cart.js lo trae. Se pide una vez y solo cuando hace falta: si hay un token apuntado
       (hay que comprobar si el pedido se hizo) o el regalo va en el carrito (hay que
       apuntarlo). Mientras se comprueba, el regalo no entra: si el pedido ya se hizo, un
       repintado de 120 ms no puede colarlo otra vez. */
    if (!base.token) {
      // Sin regalo ni token apuntado no hay nada que mirar; con regalo Y token apuntado,
      // ya está apuntado. Solo se pide en los dos casos cruzados.
      if (lleva === !!apuntado) return;
      if (this._pidiendo) return;
      this._pidiendo = true;
      if (apuntado) this._pendiente = true;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((c) => {
          this._pidiendo = false;
          this._pendiente = false;
          this.vigila(c);
          if (window.BubuCart && BubuCart._base) {
            BubuCart._deriva();
            BubuCart._pintaRapido();
            BubuCart._revisa();
          }
        })
        .catch(() => {
          this._pidiendo = false;
          this._pendiente = false;
        });
      return;
    }
    if (lleva) {
      if (base.token && apuntado !== base.token) guarda(this.token, base.token);
      return;
    }
    if (apuntado && base.token && apuntado !== base.token) this.marcaUsado();
  }
}

/* La bienvenida, con sus nombres de siempre. */
const Bienvenida = new Canje({
  clave: String(CFG.bienvenidaClave || ''),
  tipo: 'bienvenida',
  id: Number(CFG.bienvenidaId || 0),
  gratis: !!CFG.bienvenidaGratis,
  bloqueado: !!CFG.bienvenidaCanjeado,
  dias: Number(CFG.bienvenidaDias || 15),
  requiere: 'caja',
  texto: '',
  cookie: REGALO_COOKIE,
  usado: REGALO_USADO,
  token: REGALO_TOKEN,
  aviso: CFG.bienvenidaAviso || 'Tu Slime gratis entrará en tu caja con tu primer Bubu.',
  avisoUsado: CFG.bienvenidaUsado || 'Este regalo ya se ha canjeado.',
});

const Canjes = {
  lista: [Bienvenida].concat(
    (Array.isArray(CFG.canjes) ? CFG.canjes : []).map(
      (k) =>
        new Canje({
          clave: String(k.clave || ''),
          tipo: String(k.clave || ''),
          id: Number(k.id || 0),
          gratis: !!k.gratis,
          bloqueado: !!k.bloqueado,
          dias: Number(k.dias || 400),
          requiere: k.requiere === 'pack' ? 'pack' : 'caja',
          texto: k.texto || '',
          cookie: 'bubu_regalo_' + k.clave,
          // La Tarta vale una vez POR AÑO: su marca de «usado» lleva el año, así que el
          // cumple del año siguiente vuelve a poder canjearse en el mismo navegador.
          usado: 'bubu_regalo_' + k.clave + '_usado' + (Number(k.anio) ? '_' + Number(k.anio) : ''),
          token: 'bubu_regalo_' + k.clave + '_token',
          aviso: k.aviso || 'Tu regalo aparecerá gratis en tu cesta ✅',
          avisoUsado: CFG.bienvenidaUsado || 'Este regalo ya se ha canjeado.',
        })
    )
  ),
  /** Lee ?regalo=… al cargar, guarda el canje que toque y limpia la URL. */
  desdeUrl() {
    let u = null;
    try {
      u = new URL(location.href);
    } catch (_) {
      return;
    }
    const clave = u.searchParams.get('regalo');
    if (!clave) return;
    // La clave se quita de la URL para que un «recargar» o un «compartir» no la repitan.
    try {
      u.searchParams.delete('regalo');
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (_) { /* si no se puede limpiar, no pasa nada */ }
    const k = this.lista.find((x) => x.clave && x.clave === clave);
    if (k) k.desdeClave();
  },
  vigila(base) {
    this.lista.forEach((k) => k.vigila(base));
  },
};
/** Aviso con cara de regalo (misma pieza que el de bubu-caja.js, sin foto ni importe). */
function avisoRegalo(texto) {
  document.querySelectorAll('.bubu-toast-regalo').forEach((t) => t.remove());
  const t = document.createElement('div');
  t.className = 'bubu-toast-regalo';
  t.setAttribute('role', 'status');
  t.innerHTML =
    '<span class="bubu-toast-regalo__ico"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg></span>' +
    '<span class="bubu-toast-regalo__txt"><span class="bubu-toast-regalo__t"></span></span>';
  t.querySelector('.bubu-toast-regalo__t').textContent = texto;
  const cab = document.querySelector('.bubu-seccion-cabecera, .bubu-cab');
  const y = cab ? Math.min(120, Math.max(0, cab.getBoundingClientRect().bottom)) : 0;
  t.style.top = 'calc(' + Math.round(y + 12) + 'px + env(safe-area-inset-top))';
  document.body.appendChild(t);
  const fin = () => {
    if (t._bubuYendo) return;
    t._bubuYendo = true;
    t.classList.add('is-yendo');
    setTimeout(() => t.remove(), 300);
  };
  t.addEventListener('click', fin);
  setTimeout(fin, 6000);
}

/**
 * Un hueco vacío de «Crea tu caja». Se construye en UN solo sitio porque lo piden dos:
 * el ajuste de huecos y la salida de un Bubu, que deja un hueco en su lugar. Si cada uno
 * se lo montara por su cuenta, tarde o temprano dejarían de ser el mismo hueco.
 */
function nodoHueco() {
  const a = document.createElement('a');
  a.className = 'bubu-slot is-vacio';
  a.href = '#packs';
  a.setAttribute('data-bubu', 'caja-hueco');
  a.setAttribute('data-bubu-slot-vacio', '');
  a.setAttribute('aria-label', 'Elige un Bubu');
  a.innerHTML = '<span>+</span>';
  return a;
}

/**
 * LA REJILLA DE LA CAJA NO PEGA SALTOS.
 *
 * Aquí estaba el «en Monta tu caja no va unificado». En el carrito, la fila que se va
 * cierra su alto y las de abajo suben deslizándose: se lee como un solo gesto. En la
 * caja no había nada de eso: el Bubu se apagaba, el nodo se borraba de golpe y TODOS los
 * de detrás daban un salto de una casilla a la izquierda; el hueco que lo sustituye
 * aparecía además al final de la rejilla y medio segundo más tarde, cuando contestaba
 * Shopify. Una acción, tres movimientos distintos y ninguno encadenado.
 *
 * Esto es FLIP, la técnica de siempre: se apunta dónde está cada casilla ANTES de tocar
 * nada, se hace el cambio, y a cada casilla que ha terminado en otro sitio se le anima el
 * recorrido desde donde estaba. El navegador ya ha hecho el trabajo de colocarlas; lo
 * único que se añade es el viaje. Las casillas nuevas (los huecos que entran) no viajan:
 * aparecen en su sitio. Con «prefers-reduced-motion» no se anima nada y el cambio es seco.
 */
function reflujo(cont, cambia) {
  if (!cont) {
    cambia();
    return;
  }
  const antes = new Map();
  Array.from(cont.children).forEach((n) => antes.set(n, n.getBoundingClientRect()));
  cambia();
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  Array.from(cont.children).forEach((n) => {
    if (typeof n.animate !== 'function') return;
    const a = antes.get(n);
    if (!a) {
      try {
        n.animate(
          [
            { opacity: 0, transform: 'scale(.92)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 200, easing: 'cubic-bezier(.2,.8,.3,1)' }
        );
      } catch (_) { /* un adorno no puede tumbar la rejilla */ }
      return;
    }
    const b = n.getBoundingClientRect();
    const dx = Math.round(a.left - b.left);
    const dy = Math.round(a.top - b.top);
    if (!dx && !dy) return;
    try {
      n.animate(
        [{ transform: 'translate(' + dx + 'px, ' + dy + 'px)' }, { transform: 'none' }],
        { duration: 210, easing: 'cubic-bezier(.4,0,.2,1)' }
      );
    } catch (_) { /* un adorno no puede tumbar la rejilla */ }
  });
}

/** Clases de estado que pone y quita el JS, no el servidor: sobreviven al repintado. */
const TRANSITORIAS = ['is-anadido', 'is-quitando', 'is-sumando', 'is-nuevo'];
/**
 * Atributos que son del JS y el servidor no conoce. El repintado borra del nodo viejo
 * todo atributo que el nuevo no traiga, y eso se llevaba por delante `data-celda`: la
 * casilla que cada Bubu tiene reservada en el escenario de «Crea tu caja». Sin ella,
 * después de cada respuesta del servidor las celdas se repartían otra vez de cero y la
 * rejilla se recolocaba —justo lo que no puede pasar—. Ver _ajustaHuecos.
 */
const ATRIBUTOS_DEL_JS = ['data-celda'];
/**
 * ¿Es este nodo una casilla del escenario que YA lleva el JS? Si lleva `data-celda`, sí:
 * su casilla y su `order` son suyos y una respuesta del servidor no los toca. Ver el
 * comentario del bucle de atributos en morph().
 */
function llevaCasilla(el) {
  return el.nodeType === Node.ELEMENT_NODE && el.hasAttribute('data-celda');
}
/**
 * Repintado sin demoler: en vez de `innerHTML = …`, se recorre el árbol nuevo contra el
 * viejo y solo se toca lo que cambia. Las <img> con la misma URL no se sustituyen nunca,
 * así que no se vuelven a descargar ni a decodificar y no hay parpadeo. Si algo no
 * cuadra, se cae al reemplazo de siempre.
 */
function morph(viejo, nuevo) {
  if (viejo.nodeType !== nuevo.nodeType || viejo.nodeName !== nuevo.nodeName) {
    viejo.replaceWith(nuevo.cloneNode(true));
    return;
  }
  if (viejo.nodeType === Node.TEXT_NODE || viejo.nodeType === Node.COMMENT_NODE) {
    if (viejo.nodeValue !== nuevo.nodeValue) viejo.nodeValue = nuevo.nodeValue;
    return;
  }
  if (viejo.nodeType !== Node.ELEMENT_NODE) return;

  // Una imagen que ya está pintada con la misma URL no se toca.
  if (viejo.tagName === 'IMG' && viejo.getAttribute('src') === nuevo.getAttribute('src')) {
    if (viejo.alt !== nuevo.alt) viejo.alt = nuevo.alt;
    return;
  }

  /* EL ESCENARIO DE «CREA TU CAJA» NO SE REPARTE DOS VECES.
     `data-celda` y el `order` que sale de ella los escribe _ajustaHuecos, y son lo único
     que hace que un Bubu borrado deje su hueco EN SU SITIO. El servidor no puede saber
     eso: él numera de corrido, en el orden del carrito. Mientras el nodo ya tenga su
     casilla, aquí ni se le copia la del servidor ni se le borra la suya. */
  const conCasilla = llevaCasilla(viejo);
  for (const at of Array.from(nuevo.attributes)) {
    if (conCasilla && (at.name === 'data-celda' || at.name === 'style')) continue;
    if (at.name === 'class') {
      // El repintado no puede cortar una animación a medias. Las clases de estado
      // transitorio las pone y las quita el JS con su propio temporizador (BubuAnim),
      // no el servidor: si al morphear se copia el class de la respuesta tal cual, la
      // animación de añadir se corta en cuanto contesta el carrito (unos 200–400ms de
      // los 480 que dura) y se ve un parpadeo en vez de un pop. Se conservan.
      let v = at.value;
      for (const c of TRANSITORIAS) {
        if (viejo.classList.contains(c) && !nuevo.classList.contains(c)) v += ' ' + c;
      }
      if (viejo.getAttribute('class') !== v) viejo.setAttribute('class', v);
      continue;
    }
    if (viejo.getAttribute(at.name) !== at.value) viejo.setAttribute(at.name, at.value);
  }
  for (const at of Array.from(viejo.attributes)) {
    if (ATRIBUTOS_DEL_JS.indexOf(at.name) >= 0) continue;
    if (conCasilla && at.name === 'style') continue;
    if (!nuevo.hasAttribute(at.name)) viejo.removeAttribute(at.name);
  }

  morphHijos(viejo, nuevo);
}

/**
 * LA IDENTIDAD DE UNA LÍNEA NO ES SU key DE SHOPIFY.
 *
 * Aquí estaba la raíz de «los placeholders se mueven» y de buena parte de «no va
 * fluido». La `key` de una línea del carrito NO es estable en esta tienda: en cuanto el
 * carrito cruza un tramo de la escalera (3, 4 o 5 Bubus) cambia el descuento de todas
 * las líneas y Shopify les da una key NUEVA a todas. Medido contra /cart/update.js sin
 * pasar por la interfaz: al quitar un Bubu de cuatro, las TRES que quedaban cambiaron de
 * key; ninguna se salvó. Al añadir sin cruzar tramo, ninguna cambia. O sea que el
 * momento exacto en que la barra de hitos canta un logro es el momento en que todas las
 * líneas se vuelven irreconocibles.
 *
 * Y el repintado empareja por esa clave. Sin reconocer ni una: tiraba todos los nodos,
 * construía otros nuevos, las fotos volvían a entrar, las animaciones arrancaban de
 * cero y —lo que se ve— cada Bubu perdía su casilla y la rejilla se recolocaba entera.
 *
 * `grupoDom` es variante + propiedades, que es EXACTAMENTE el criterio con el que
 * Shopify junta dos líneas en una: por construcción no hay dos líneas con el mismo
 * grupo, y no lo mueve ningún descuento. La key se sigue leyendo y actualizando porque
 * es lo que hay que mandarle a /cart/update.js, pero ya no decide quién es quién.
 *
 * El orden de las tres propiedades es fijo (regalo, lote, pack) y el mismo que escriben
 * los snippets de Liquid. Si algún día se añade una propiedad que distinga dos líneas,
 * va aquí Y allí, en los dos sitios.
 */
function grupoDom(item, items) {
  const p = (item && item.properties) || {};
  const g = [item.variant_id, p._regalo || '', p._lote || '', p._pack_id || ''].join('|');
  if (!items) return g;
  // Shopify parte una misma variante en dos líneas cuando un descuento solo toca a una
  // parte (Coleccionista). Para que no compartan identidad, la segunda y siguientes van
  // numeradas («|1», «|2») en el MISMO orden en que las pinta Liquid (el carrito al
  // revés): se cuentan las iguales que van DETRÁS en el carrito (09/10/2026).
  let n = 0;
  for (let i = items.length - 1; i >= 0 && items[i] !== item; i--) {
    if (grupoDom(items[i]) === g) n++;
  }
  return n ? g + '|' + n : g;
}

/** Identidad de un hijo: lo que lo hace ÉL y no el de al lado. */
function claveNodo(n) {
  if (!n || n.nodeType !== Node.ELEMENT_NODE) return null;
  return n.getAttribute('data-grupo') || n.getAttribute('data-key') || n.getAttribute('id') || null;
}

/**
 * LAS LISTAS SE EMPAREJAN POR IDENTIDAD, NO POR POSICIÓN.
 *
 * Aquí estaba el «borras y vuelven a salir». El repintado recorría los hijos viejos y
 * los nuevos EN PARALELO por índice: hijo 0 contra hijo 0, hijo 1 contra hijo 1. Eso
 * vale para una caja con un título y un botón, pero no para una lista de la que entran
 * y salen líneas. Si tenías [A, B, C] y quitabas B, la pantalla se quedaba con [A, C]
 * y la respuesta del servidor —que salió antes de tu toque— traía [A, B, C]: entonces
 * la fila C se emparejaba con B y se le reescribía la key, el nombre y la foto, y al
 * final se añadía una C nueva. Resultado: líneas que cambian de Bubu, una borrada que
 * reaparece con la foto de otra y la sensación de que la caja hace lo que le da la gana.
 *
 * Con clave, cada fila se empareja con la suya, se mueve si ha cambiado de sitio (sin
 * volver a crearla, así que la foto no se descarga otra vez) y la que sobra se va. Es
 * lo mismo que hace cualquier librería de listas, y por el mismo motivo.
 */
function morphHijos(viejo, nuevo) {
  const nuevos = Array.from(nuevo.childNodes);
  // Las que se están yendo ya no existen para el repintado: no se emparejan, no se
  // reciclan y no se reordenan. Se borran solas cuando acaba su animación.
  const viejos = Array.from(viejo.childNodes).filter(
    (n) => !(n.nodeType === 1 && n.hasAttribute('data-bubu-saliendo'))
  );

  // Sin claves no hay nada que ganar: se sigue emparejando por posición, que para un
  // bloque de estructura fija es lo correcto y lo más barato.
  if (!nuevos.some(claveNodo)) {
    const n = Math.min(viejos.length, nuevos.length);
    for (let i = 0; i < n; i++) morph(viejos[i], nuevos[i]);
    for (let i = n; i < viejos.length; i++) viejos[i].remove();
    for (let i = n; i < nuevos.length; i++) viejo.appendChild(nuevos[i].cloneNode(true));
    return;
  }

  const porClave = new Map();
  viejos.forEach((n) => {
    const k = claveNodo(n);
    if (k && !porClave.has(k)) porClave.set(k, n);
  });
  const sueltos = viejos.filter((n) => !claveNodo(n));
  const orden = [];
  let s = 0;
  nuevos.forEach((n) => {
    const k = claveNodo(n);
    const igual = k ? porClave.get(k) : sueltos[s++];
    if (k && igual) porClave.delete(k);
    if (igual) {
      morph(igual, n);
      orden.push(igual);
    } else {
      orden.push(n.cloneNode(true));
    }
  });
  porClave.forEach((n) => n.remove());
  for (let i = s; i < sueltos.length; i++) sueltos[i].remove();
  // Colocar: solo se toca el que de verdad está fuera de sitio.
  let ant = null;
  orden.forEach((nodo) => {
    const donde = ant ? ant.nextSibling : viejo.firstChild;
    if (donde !== nodo) viejo.insertBefore(nodo, donde);
    ant = nodo;
  });
}

/* ------------------------------------------------------------------
   Salida de una línea que ya no está en el carrito.

   Los betatesters veían el Bubu desaparecer y volver: el nodo se borraba de
   golpe, el servidor contestaba más tarde y el repintado lo traía otra vez.
   Aquí el nodo se marca como saliente y PIERDE sus señas (data-key,
   data-bubu-linea, data-bubu-slot), así que ningún repintado posterior lo
   vuelve a encontrar ni lo reutiliza: cuando termina la animación se va, y no
   vuelve. Si el cliente añade otra vez el mismo Bubu, se pinta un nodo nuevo.

   La animación es corta y discreta (estilo Gymshark): encoge un poco, se
   apaga y la fila cierra su alto. Con "prefers-reduced-motion" se va sin más.
   ------------------------------------------------------------------ */
function sale(el, fila) {
  if (!el || el.hasAttribute('data-bubu-saliendo')) return;
  el.setAttribute('data-bubu-saliendo', '');
  el.removeAttribute('data-bubu-linea');
  el.removeAttribute('data-bubu-slot');
  el.removeAttribute('data-key');
  el.removeAttribute('data-grupo');
  el.style.pointerEvents = 'none';
  el.querySelectorAll('button, a, input').forEach((c) => { c.disabled = true; c.setAttribute('tabindex', '-1'); });
  // Una casilla de la caja no se borra y ya: deja un hueco en su sitio y la rejilla se
  // recoloca deslizándose (reflujo). Una fila del carrito cierra su alto, que es lo que
  // ya hacía. Las dos cosas son el mismo gesto contado en el idioma de cada superficie.
  const contSlots = fila ? null : el.closest('[data-bubu-slots]');
  // El final llega por dos caminos (el fin de la animación y el plazo de seguridad) y
  // solo puede contar el primero: recolocar la rejilla dos veces se ve.
  let ido = false;
  const fin = () => {
    if (ido) return;
    ido = true;
    clearTimeout(el._bubuSalT);
    if (!contSlots) { el.remove(); return; }
    reflujo(contSlots, () => {
      el.remove();
      try { BubuCart._ajustaHuecos(); } catch (_) { /* nunca por encima del borrado */ }
    });
  };
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) { fin(); return; }
  const cuadros = fila
    ? [
        { opacity: 1, transform: 'translateX(0) scale(1)' },
        { opacity: 0, transform: 'translateX(-10px) scale(.98)' },
      ]
    : [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(.86)' },
      ];
  let anim = null;
  try {
    anim = el.animate(cuadros, { duration: 190, easing: 'cubic-bezier(.4,0,.6,1)', fill: 'forwards' });
  } catch (e) { fin(); return; }
  if (fila) {
    // El alto se cierra después de apagarse, para que la lista no pegue el tirón
    // a la vez que el contenido se va.
    const alto = el.getBoundingClientRect().height;
    const est = getComputedStyle(el);
    anim.onfinish = () => {
      el.style.overflow = 'hidden';
      el.style.height = alto + 'px';
      el.style.marginTop = est.marginTop;
      el.style.marginBottom = est.marginBottom;
      el.style.paddingTop = est.paddingTop;
      el.style.paddingBottom = est.paddingBottom;
      let cierre = null;
      try {
        cierre = el.animate(
          [
            { height: alto + 'px', marginTop: est.marginTop, marginBottom: est.marginBottom, paddingTop: est.paddingTop, paddingBottom: est.paddingBottom, opacity: 0 },
            { height: '0px', marginTop: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
          ],
          { duration: 170, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' }
        );
      } catch (e) { fin(); return; }
      cierre.onfinish = fin;
    };
    el._bubuSalT = setTimeout(fin, 900);
  } else {
    anim.onfinish = fin;
    el._bubuSalT = setTimeout(fin, 700);
  }
}

export const BubuAnim = {
  grande(btn) {
    if (!btn) return;
    if (!btn.querySelector('.bubu-barrido')) {
      const s = document.createElement('span');
      s.className = 'bubu-barrido';
      btn.appendChild(s);
    }
    btn.classList.remove('is-anadido');
    void btn.offsetWidth;
    btn.classList.add('is-anadido');
    clearTimeout(btn._bubuT);
    btn._bubuT = setTimeout(() => btn.classList.remove('is-anadido'), 900);
  },
  mas(btn) {
    if (!btn) return;
    btn.classList.remove('is-anadido');
    void btn.offsetWidth;
    btn.classList.add('is-anadido');
    clearTimeout(btn._bubuT);
    btn._bubuT = setTimeout(() => btn.classList.remove('is-anadido'), 480);
  },
};

// Regalo = producto que YA vale 0 € (activador, Mini, Mystery, Legendario, Tarta de
// regalo) o línea marcada con _regalo. Se mira el precio ORIGINAL, no el final: un Bubu
// que sale a 0 € por un código (Coleccionista) es un Bubu de verdad y Shopify lo cuenta
// para los tramos, así que aquí también cuenta (09/10/2026). Misma regla en Liquid.
function esRegalo(item) {
  return item.original_line_price === 0 || !!(item.properties && item.properties._regalo);
}
function esPack(item) {
  return !!(item.properties && item.properties._pack_id);
}
/**
 * Pulso al RESTAR una unidad. En el diseño, bubuQuita encoge la línea al 0,9 con
 * opacidad 0,75 y vuelve a su sitio: es acuse de recibo del «−», no una desaparición
 * (la curva termina en opacity 1). Si la línea llega a cero, se va sin animación.
 */
export function BubuQuita(el) {
  if (!el) return;
  el.classList.remove('is-quitando');
  void el.offsetWidth;
  el.classList.add('is-quitando');
  clearTimeout(el._bubuQ);
  el._bubuQ = setTimeout(() => el.classList.remove('is-quitando'), 420);
}

/** Lote cerrado (Caja Secreta): una línea a precio de lote, fuera de la escalera. */
function esLote(item) {
  return !!(item.properties && item.properties._lote);
}

/**
 * Unidades que CUENTAN en un carrito cualquiera: las mismas que mira la barra de hitos y
 * las mismas que mira el descuento automático de Shopify.
 *
 * Vive suelta, fuera del modelo, porque la necesitan dos sitios con carritos distintos:
 * `_uds()` cuenta sobre el carrito vigente y `_activadorEnModelo` sobre el carrito
 * candidato que todavía no existe. Cuando cada uno contaba por su cuenta, el regalo y la
 * barra podían decir cosas distintas sobre el mismo carrito; ahora no pueden.
 */
function udsCuentan(items) {
  const fuera = Array.isArray(CFG.noSuman) ? CFG.noSuman : [];
  return (items || []).reduce(
    (a, i) => (esPack(i) || esRegalo(i) || esLote(i) || fuera.indexOf(Number(i.variant_id)) >= 0 ? a : a + i.quantity),
    0
  );
}

/**
 * LO QUE CUESTA EL SIGUIENTE BUBU, EN EUROS (05/10/2026). «El tercero te sale por
 * 9,78 €» convence más que «ahorras un 10 %»: es lo que pagas de más por llevarte uno
 * más, contando que al subir de tramo TODA la caja se recalcula con el nuevo descuento.
 * Mismo redondeo por unidad que _deriva y que Shopify (13,95 × 0,9 = 12,555 → 12,56).
 * La MISMA cuenta está en snippets/bubu-siguiente.liquid: el servidor y el clic tienen
 * que decir el mismo número o el texto parpadea al contestar Shopify.
 * `base` es el precio del Bubu que se añadiría (el de la ficha, o el base de la tienda).
 */
function cuestaSiguiente(items, pctNuevo, base) {
  // Con un Bubu gratis por código (Coleccionista) Shopify reparte los descuentos de otra
  // forma y esta cuenta no sería exacta: 0 = se queda el texto de siempre. Misma regla
  // que snippets/bubu-siguiente.liquid.
  if ((items || []).some((i) => !i._provisional && i.final_price === 0 && (i.original_price || 0) > 0 && !esRegalo(i))) return 0;
  const fuera = Array.isArray(CFG.noSuman) ? CFG.noSuman : [];
  const cuenta = (i) => !(esPack(i) || esRegalo(i) || esLote(i) || fuera.indexOf(Number(i.variant_id)) >= 0);
  const E = CFG.escalera || [{ uds: 3, pct: 10 }, { uds: 4, pct: 0 }, { uds: 5, pct: 15 }];
  const r = (b, p) => (p ? Math.floor((b * (100 - p) + 50) / 100) : b);
  let uds = 0;
  (items || []).forEach((i) => { if (cuenta(i)) uds += i.quantity; });
  let pctAct = 0;
  E.forEach((t) => { if (uds >= t.uds && t.pct > pctAct) pctAct = t.pct; });
  let antes = 0;
  let despues = 0;
  (items || []).forEach((i) => {
    if (!cuenta(i)) return;
    const b = i.original_price || i.price || 0;
    antes += r(b, pctAct) * i.quantity;
    despues += r(b, pctNuevo) * i.quantity;
  });
  const b0 = Number(base) || Number(CFG.precioBase) || 1395;
  return despues + r(b0, pctNuevo) - antes;
}
const ORDINAL = { 2: 'segundo', 3: 'tercero', 4: 'cuarto', 5: 'quinto', 6: 'sexto' };

/**
 * Errores que NO son culpa del cliente ni le dicen nada: los que salen cuando la
 * reconciliación llega tarde y manda una línea que ya no existe. Se resuelven solos
 * volviendo a pedir el carrito, así que no se le enseña un aviso negro por la cara.
 */
function esRuidoDeCarrito(e) {
  const m = ((e && (e.description || e.message)) || '').toLowerCase();
  return m.indexOf('updates') >= 0 || m.indexOf('line item') >= 0 || m.indexOf('parámetro') >= 0 || m.indexOf('parametro') >= 0;
}

const BubuCart = {
  /**
   * EL MODELO SE DERIVA, NO SE ACUMULA.
   *
   * Antes esto sumaba y restaba cantidades sobre `this.cart` a cada clic y luego
   * intentaba «volver a aplicar» lo que faltaba encima de cada respuesta del servidor.
   * Con toques rápidos era imposible no descuadrarse: bastaba con que una respuesta
   * llegara en mitad de otra para contar dos veces lo mismo, y la barra de hitos se
   * plantaba en «Máximo ahorro» con un solo Bubu en la caja.
   *
   * Ahora hay dos cosas y solo dos:
   *   _base → el último carrito que ha confirmado Shopify. No se toca jamás a mano.
   *   _ops  → lo que el cliente ha pulsado y el servidor todavía no ha confirmado.
   * `cart` (lo que se pinta) se RECALCULA entero desde esas dos, así que contar de más
   * es imposible por construcción: si una operación ya está en _base, deja de estar en
   * _ops en el mismo instante.
   *
   * Y no se manda «lo que pulsaste» sino LA DIFERENCIA entre lo que quieres y lo que
   * hay (_plan). Añadir cinco y quitarlos de seguido no sale a la red; quitar líneas
   * usa siempre keys que existen de verdad en _base (de ahí venía el «El parámetro
   * updates no es válido»); y el activador de regalo viaja en la misma petición en vez
   * de en una carrera aparte, que era lo que descolocaba todo lo demás.
   */
  cart: null,
  _base: null,
  _ops: [],
  _n: 0,
  _t: null,

  async _trae() {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return r.json();
  },
  /**
   * El carrito que viaja dentro del HTML de la sección bubu-cesta (ver el comentario de
   * sections/bubu-cesta.liquid). Sirve tanto para arrancar sin pedir /cart.js como para
   * ahorrarse el segundo viaje después de cada operación.
   */
  _deHtml(html) {
    if (!html) return null;
    try {
      const tmp = document.createElement('template');
      tmp.innerHTML = html;
      const n = tmp.content.querySelector('[data-bubu-cart-json]');
      const c = n ? JSON.parse(n.textContent) : null;
      return c && Array.isArray(c.items) ? c : null;
    } catch (_) {
      return null;
    }
  },
  _deSecciones(secciones) {
    if (!secciones) return null;
    return this._deHtml(secciones['bubu-cesta']);
  },
  /** El de la página actual, el que pintó Liquid al cargar. */
  _delDocumento() {
    const n = document.querySelector('[data-bubu-cart-json]');
    if (!n) return null;
    try {
      const c = JSON.parse(n.textContent);
      return c && Array.isArray(c.items) ? c : null;
    } catch (_) {
      return null;
    }
  },

  /** Sincroniza con el servidor. Solo se llama desde dentro de la cola. */
  async get() {
    this._base = await this._trae();
    this._ops = this._ops.filter((o) => !o.enviada);
    this._limpiaOps();
    Canjes.vigila(this._base);
    this._deriva();
    return this.cart;
  },

  lineasNormales() {
    return this.cart ? this.cart.items.filter((i) => !esRegalo(i) && !esPack(i)) : [];
  },

  // ---------- Modelo derivado ----------
  _deriva() {
    if (!this._base) {
      this.cart = null;
      return;
    }
    const c = Object.assign({}, this._base);
    c.items = this._base.items.map((i) => Object.assign({}, i));
    const hubo = this._ops.length > 0;
    this._ops.forEach((op) => {
      if (op.t === 'add') this._ponAdd(c, op);
      else if (op.t === 'menos') this._ponMenos(c, op);
      else this._ponSet(c, op);
    });
    const antes = c.items.length;
    this._activadorEnModelo(c);
    if (hubo || c.items.length !== antes) this._totales(c);
    this.cart = c;
  },

  _ponAdd(c, op) {
    // Se suma a la línea de pago de esa variante, no a la que un código deja a 0 €
    // (Coleccionista): si no, el Bubu nuevo se pintaría gratis hasta que contesta Shopify.
    const linea = op.props ? null : c.items.find((i) => Number(i.variant_id) === op.vid && !esRegalo(i) && !esPack(i) && !(i.final_price === 0 && !i._provisional));
    if (linea) {
      linea.quantity += op.qty;
      return;
    }
    const p = op.pistas || {};
    const precio = Number(p.precio) || Number(CFG.precioBase || 0);
    c.items.push({
      key: 'bubu-op-' + op.n,
      id: op.vid,
      variant_id: op.vid,
      product_id: 0,
      quantity: op.qty,
      price: precio,
      final_price: precio,
      original_price: precio,
      final_line_price: precio * op.qty,
      original_line_price: precio * op.qty,
      properties: op.props || null,
      image: p.img || null,
      imageCaja: p.imgCaja || p.img || null,
      imageCesta: p.imgCesta || p.img || null,
      url: '#',
      product_title: p.titulo || '',
      _provisional: true,
      _op: op.n,
    });
  },

  /**
   * Quitar unidades de una línea que TODAVÍA no tiene key del servidor.
   *
   * Era el último agujero del borrado rápido: si el alta ya había salido a la red, la
   * «×» no tenía nada que cancelar y el toque se perdía —el Bubu se quedaba puesto y
   * parecía que «volvía»—. Ahora la intención se apunta igual, sin key: descuenta de la
   * línea que coincida en variante y propiedades, sea provisional o ya real. Se pinta en
   * el acto y, cuando el alta aterriza, el reconciliador manda el update que toca.
   */
  _ponMenos(c, op) {
    let quedan = op.qty;
    for (let i = c.items.length - 1; i >= 0 && quedan > 0; i--) {
      const l = c.items[i];
      if (esRegalo(l) || esPack(l)) continue;
      if (this._grupo(l.variant_id, l.properties) !== this._grupo(op.vid, op.props)) continue;
      const baja = Math.min(l.quantity, quedan);
      l.quantity -= baja;
      quedan -= baja;
      if (l.quantity <= 0) c.items.splice(i, 1);
    }
  },

  _ponSet(c, op) {
    // Por key y, si la key ya no está (ha cambiado con un tramo), por grupo: es la misma
    // línea, solo que Shopify le ha puesto otro nombre por el camino.
    let i = c.items.findIndex((l) => l.key === op.key);
    if (i < 0 && op.grupo) i = c.items.findIndex((l) => this._grupo(l.variant_id, l.properties) === op.grupo);
    if (i < 0) return;
    if (op.qty <= 0) c.items.splice(i, 1);
    else c.items[i].quantity = op.qty;
  },

  /**
   * El activador de regalo es un DESEO del modelo, no una petición aparte. Antes se
   * lanzaba al terminar de añadir, fuera de la cola, y se colaba entre los toques del
   * cliente: refrescaba el carrito a destiempo y mandaba borrados de líneas que ya no
   * existían. Aquí solo se dice «en un carrito con Bubus tiene que haber exactamente un
   * activador a 0 €», y el reconciliador ya lo mete o lo quita en el mismo viaje.
   */
  _activadorEnModelo(c) {
    const normales = c.items.filter((i) => !esRegalo(i)).length;
    if (normales === 0) {
      if (c.items.some(esRegalo)) c.items = c.items.filter((i) => !esRegalo(i));
      return;
    }
    const act = c.items.find((i) => i.properties && i.properties._regalo === 'activador');
    /* EL ACTIVADOR SE REGALA CON LOS SLIMES. CON NADA MÁS.
       Dos cosas que NO lo traen:
       · Un pack de fiesta. Ya lleva los suyos dentro del precio —«2 activadores para el
         adulto» en el Pack Fiesta, «activador individual por bolsita» en el Premium—, así
         que sumarle otro a 0 € es regalar algo que el cliente ya ha pagado.
       · Un accesorio «no-cuenta»: el Activador de 4,95 € y el Bubu Care de 0,95 €. Aquí
         estaba el agujero: con un Bubu Care de 0,95 € en el carrito entraba un activador
         de regalo. Comprar un activador y que te regalen otro no hay por dónde cogerlo.
       Lo que SÍ lo trae es cualquier cosa que cuente como caja: un slime, un Mystery, una
       Tarta o un lote cerrado. La lista de lo que no cuenta llega de Liquid en
       snippets/bubu-config.liquid (etiqueta «no-cuenta»), la misma que usa la escalera,
       así que el regalo y la barra de hitos no pueden contradecirse.
       Se comprueba antes de mirar la configuración a propósito: aunque hoy el ajuste del
       activador esté vacío, un activador heredado de una sesión anterior también sale. */
    const noCuentan = Array.isArray(CFG.noSuman) ? CFG.noSuman : [];
    const deTienda = c.items.filter(
      (i) => !esRegalo(i) && !esPack(i) && noCuentan.indexOf(Number(i.variant_id)) < 0
    ).length;
    const lineaDe = (tipo) => c.items.find((i) => i.properties && i.properties._regalo === tipo);
    if (deTienda === 0) {
      /* Sin nada de tienda se van el activador y los regalos que piden caja. Los que
         piden un cumple (la Tarta) se miran abajo: un Pack Fiesta solo no es «tienda». */
      const fuera = [act].concat(Canjes.lista.filter((k) => k.requiere !== 'pack').map((k) => lineaDe(k.tipo)));
      c.items = c.items.filter((i) => fuera.indexOf(i) < 0);
      this._canjesEnModelo(c, lineaDe, false);
      return;
    }
    /* EL ACTIVADOR PIDE UN MÍNIMO DE BUBUS (2 desde el 21/09/2026).
       Regalarlo con una sola unidad no compraba comportamiento: apagó el upsell del
       activador de pago —pasó de salir en el 27 % de los pedidos al 4 %— sin mover el
       tamaño de la caja. Con el mínimo en 2 vuelve a ser un premio por el que merece la
       pena añadir algo, y es el premio que le faltaba al carrito de dos desde que el
       −10 % subió a tres unidades.
       Se mide en UNIDADES, no en líneas, y con la misma cuenta que la barra de hitos
       (udsCuentan): dos Bubus en una sola línea con cantidad 2 cuentan igual que dos
       líneas de uno. El mínimo llega de los ajustes del tema, así que se puede volver a
       1 sin tocar código si el experimento sale mal.
       Y baja igual que sube: si el carrito se queda por debajo del mínimo, la línea del
       activador SE RETIRA. Un regalo que se queda puesto después de perder el derecho es
       una promesa rota en el checkout. */
    /* LA CAJA SECRETA ES LA EXCEPCIÓN. Un lote cerrado entra como UNA línea aunque
       dentro lleve cuatro Bubus, así que contando unidades se quedaría en 1 y perdería
       el activador que hoy sí lleva. Decisión de Álvaro (21/09/2026): la Caja Secreta lo
       sigue llevando. El Pack Fiesta no se cuela por aquí: ese entra con _pack_id desde
       la página de cumples, y `esPack` ya lo deja fuera unas líneas más arriba. */
    const uds = udsCuentan(c.items);
    const hayLote = c.items.some(esLote);
    const minAct = Math.max(1, Number(CFG.activadorMin || 1));
    const id = Number(CFG.activadorId || 0);
    if (id && CFG.activadorGratis && (uds >= minAct || hayLote)) {
      if (!act) c.items.push(this._lineaRegalo('bubu-activador', id, 'activador'));
      else if (act.quantity > 1) act.quantity = 1;
    } else if (act) {
      c.items = c.items.filter((i) => i !== act);
    }
    this._canjesEnModelo(c, lineaDe, true);
  },

  /**
   * LOS REGALOS DE LOS CORREOS (bienvenida, mystery, leyenda, tarta). Misma mecánica que
   * el activador, con la diferencia de que solo entran si hay un canje vigente en este
   * navegador (Canje.activa, ver arriba). Si el canje se ha usado o no existe, una línea
   * heredada de una sesión anterior se queda como está: quitarla sería quitarle a
   * alguien un regalo que Shopify ya le dio. Lo que sí se quita es el regalo que pierde
   * lo que pide: el de caja sin Bubus de pago, la Tarta sin cumple.
   * Y el regalo YA CANJEADO (10/10/2026): si la clienta ha iniciado sesión y tiene la
   * etiqueta de ese regalo, se quita aunque viniera de antes de iniciar sesión.
   */
  _canjesEnModelo(c, lineaDe, hayCaja) {
    const hayPack = c.items.some(esPack);
    Canjes.lista.forEach((k) => {
      const linea = lineaDe(k.tipo);
      const puede = k.requiere === 'pack' ? hayPack : hayCaja;
      if (!puede || k.bloqueado) {
        if (linea) c.items = c.items.filter((i) => i !== linea);
        return;
      }
      if (!k.id || !k.activa()) return;
      if (!linea) c.items.push(this._lineaRegalo('bubu-' + k.tipo, k.id, k.tipo, k.texto));
      else if (linea.quantity > 1) linea.quantity = 1;
    });
  },

  /** Línea provisional de un regalo a 0 €: la pinta el modelo hasta que Shopify la confirma. */
  _lineaRegalo(key, vid, tipo, texto) {
    const props = { _regalo: tipo };
    if (texto) props._regalo_texto = texto;
    return {
      key: key,
      id: vid,
      variant_id: vid,
      product_id: 0,
      quantity: 1,
      price: 0,
      final_price: 0,
      original_price: 0,
      final_line_price: 0,
      original_line_price: 0,
      properties: props,
      image: null,
      url: '#',
      product_title: '',
      _provisional: true,
    };
  },

  _totales(c) {
    /* LA ESCALERA TAMBIÉN SE PINTA EN EL ACTO. Antes el total provisional sumaba los
       precios tal cual (el tercer Bubu entraba a 13,95 y los dos de antes seguían a
       13,95), Shopify contestaba con el −10 % y el total, «Estás ahorrando» y la barra
       de hitos daban un salto hacia atrás medio segundo después del clic. Ahora el
       modelo aplica el mismo tramo y el MISMO redondeo que Shopify: por unidad, a la
       centésima más cercana (13,95 × 0,9 = 12,555 → 12,56), así que lo que se pinta al
       tocar es lo mismo que confirma el servidor. Con un pack de cumple dentro no se
       modela nada: Shopify aplica solo el mayor de los dos descuentos y ese cálculo se
       deja al servidor, como hasta ahora. */
    const fuera = Array.isArray(CFG.noSuman) ? CFG.noSuman : [];
    const cuenta = (i) => !(esPack(i) || esRegalo(i) || esLote(i) || fuera.indexOf(Number(i.variant_id)) >= 0);
    const hayPack = c.items.some(esPack);
    let uds = 0;
    c.items.forEach((i) => { if (cuenta(i)) uds += i.quantity; });
    const E = CFG.escalera || [{ uds: 3, pct: 10 }, { uds: 4, pct: 0 }, { uds: 5, pct: 15 }];
    let pct = 0;
    E.forEach((t) => { if (uds >= t.uds && t.pct > pct) pct = t.pct; });
    c.items.forEach((i) => {
      const base = i.original_price || i.price || 0;
      // Un Bubu que Shopify ya deja a 0 € con un código (Coleccionista) sigue a 0 €: el
      // tramo no se le aplica encima. Cuenta para las unidades, pero su precio es el suyo.
      const gratisPorCodigo = !i._provisional && i.final_price === 0 && base > 0;
      if (!hayPack && base > 0 && cuenta(i) && !gratisPorCodigo) {
        i.final_price = pct ? Math.floor((base * (100 - pct) + 50) / 100) : base;
      }
      i.final_line_price = (i.final_price || i.price || 0) * i.quantity;
      i.original_line_price = (i.original_price || i.price || 0) * i.quantity;
    });
    c.item_count = c.items.reduce((a, i) => a + i.quantity, 0);
    c.total_price = c.items.reduce((a, i) => a + (i.final_line_price || 0), 0);
    c.original_total_price = c.items.reduce((a, i) => a + ((i.original_price || i.price || 0) * i.quantity), 0);
  },

  // ---------- Intenciones del cliente ----------
  _apunta(op) {
    op.n = ++this._n;
    this._ops.push(op);
  },

  /**
   * TODOS los «añadir» de la interfaz pasan por aquí. Se pinta en el acto y la petición
   * espera 120 ms: si en ese rato caen más toques, salen todos en el mismo viaje.
   */
  pideAnadir(items, origen) {
    if (!this._base) {
      conBloqueo(async () => {
        await this.get();
        this.pideAnadir(items, origen);
      });
      return;
    }
    const tClic = performance.now();
    const antesVacio = this.lineasNormales().length === 0 && !this.cart.items.some(esPack);
    const antesActivador = this._hayActivador();
    const pistas = this._pistas(origen);
    // Un item puede traer sus propias pistas (foto y nombre): los packs de componentes
    // las mandan por Bubu, para que cada línea provisional salga ya con SU slime y no
    // con la foto del pack. Sin ellas, se usan las de la cuna del botón, como siempre.
    items.forEach((it) =>
      this._apunta({ t: 'add', vid: Number(it.id), qty: it.quantity || 1, props: it.properties || null, pistas: it.pistas || pistas })
    );
    this._deriva();
    this._pintaRapido();
    // Segundo motivo para abrir la hoja (21/09/2026): el añadido que GANA el activador.
    // Ver _trasAnadir.
    const ganaActivador = !antesActivador && this._hayActivador();
    this._trasAnadir(antesVacio || ganaActivador, origen, tClic);
    this._programa();
  },

  /** ¿Está ya el activador de regalo en el modelo? */
  _hayActivador() {
    const id = Number(CFG.activadorId || 0);
    return !!(id && this.cart && this.cart.items.some((i) => esRegalo(i) && Number(i.variant_id) === id));
  },

  /**
   * Cambiar la cantidad de una línea. Vale igual para una línea de verdad y para una
   * que todavía no existe en el servidor: como lo que se manda es la diferencia, quitar
   * algo recién añadido simplemente hace que no llegue a salir.
   */
  pideCambio(key, qty) {
    if (typeof key !== 'string' || !this.cart) return;
    const l = this.linea(key);
    if (!l) return;
    const n = Math.max(0, Number(qty) || 0);
    if (l._provisional && l._op) {
      const baja = l.quantity - n;
      if (baja > 0) {
        // Primero se intenta que no llegue a salir; lo que ya viaja se apunta como
        // «menos» para cobrarlo en cuanto la línea exista de verdad.
        const resto = baja - this._quitaDeOps(l.variant_id, l.properties, baja);
        if (resto > 0) this._apunta({ t: 'menos', vid: Number(l.variant_id), props: l.properties || null, qty: resto });
      } else if (baja < 0) {
        this._apunta({ t: 'add', vid: Number(l.variant_id), qty: -baja, props: l.properties || null, pistas: null });
      }
    } else {
      // La intención lleva TAMBIÉN el grupo (variante + propiedades), no solo la key.
      // Ver _limpiaOps: la key cambia sola cuando el carrito cruza un tramo, y una
      // baja apuntada solo por key se perdía en cuanto contestaba la petición anterior.
      this._apunta({ t: 'set', key: key, qty: n, grupo: this._grupo(l.variant_id, l.properties) });
    }
    this._deriva();
    this._pintaRapido();
    this._programa();
  },

  /**
   * Descuenta unidades de las altas que aún NO han salido a la red y devuelve cuántas
   * ha podido cancelar. Compara por grupo (variante + propiedades), así que también
   * vale para un lote cerrado, que viaja con _lote.
   */
  _quitaDeOps(variantId, props, unidades) {
    const g = this._grupo(variantId, props);
    let quedan = unidades;
    for (let i = this._ops.length - 1; i >= 0 && quedan > 0; i--) {
      const o = this._ops[i];
      if (o.t !== 'add' || o.enviada) continue;
      if (this._grupo(o.vid, o.props) !== g) continue;
      const baja = Math.min(o.qty, quedan);
      o.qty -= baja;
      quedan -= baja;
      if (o.qty <= 0) this._ops.splice(i, 1);
    }
    return unidades - quedan;
  },

  // ---------- Reconciliación con Shopify ----------
  _programa(ms) {
    clearTimeout(this._t);
    this._t = setTimeout(() => conBloqueo(() => this._concilia()), ms === undefined ? 120 : ms);
  },

  /** Fuerza el viaje ya y devuelve una promesa que espera a que termine. */
  _ahora() {
    clearTimeout(this._t);
    return conBloqueo(() => this._concilia());
  },

  /**
   * Clave de agrupación de una línea: misma variante Y mismas propiedades. Shopify junta
   * en una sola línea todo lo que comparte esta clave, así que es la unidad con la que
   * hay que razonar tanto para mandar diferencias como para descontar unidades.
   */
  _grupo(vid, props) {
    const p = props;
    const ks = p ? Object.keys(p).filter((k) => p[k] !== null && p[k] !== undefined && p[k] !== '').sort() : [];
    return Number(vid) + '|' + ks.map((k) => k + '=' + p[k]).join('&');
  },

  /**
   * La diferencia entre lo que el cliente quiere (cart) y lo que hay de verdad (_base).
   * Las líneas se agrupan por variante + propiedades, así que una misma variante
   * añadida cinco veces es una sola entrada con quantity 5.
   */
  _plan() {
    const grupo = (i) => this._grupo(i.variant_id, i.properties);
    const quiero = new Map();
    this.cart.items.forEach((i) => {
      const g = grupo(i);
      const e = quiero.get(g) || { qty: 0, props: i.properties || null, vid: Number(i.variant_id) };
      e.qty += i.quantity;
      quiero.set(g, e);
    });
    const tengo = new Map();
    this._base.items.forEach((i) => {
      const g = grupo(i);
      const e = tengo.get(g) || { qty: 0, lineas: [] };
      e.qty += i.quantity;
      e.lineas.push(i);
      tengo.set(g, e);
    });
    const add = [];
    const updates = {};
    quiero.forEach((q, g) => {
      const t = tengo.get(g);
      const hay = t ? t.qty : 0;
      if (q.qty > hay) add.push({ id: q.vid, quantity: q.qty - hay, properties: q.props || undefined });
    });
    tengo.forEach((t, g) => {
      const q = quiero.get(g);
      const quiere = q ? q.qty : 0;
      if (quiere >= t.qty) return;
      let sobra = t.qty - quiere;
      for (let i = t.lineas.length - 1; i >= 0 && sobra > 0; i--) {
        const l = t.lineas[i];
        const baja = Math.min(l.quantity, sobra);
        updates[l.key] = l.quantity - baja;
        sobra -= baja;
      }
    });
    return { add: add, updates: updates };
  },

  /**
   * Apunta en el carrito qué pack se ha añadido (atributo «Packs añadidos»), para los
   * informes (28/09/2026). Los packs de componentes entran como slimes sueltos y en el
   * pedido no quedaba rastro del pack. Es un atributo del carrito, NO una propiedad de
   * línea: así no parte líneas ni toca la cesta, la escalera ni el activador. El
   * cliente no lo ve; sale en «Detalles adicionales» del pedido. Si falla, no pasa nada.
   */
  async marcaPack(nombre) {
    try {
      const clave = 'Packs añadidos';
      const attrs = (this.cart && this.cart.attributes) || {};
      const lista = String(attrs[clave] || '').split(',').map((x) => x.trim()).filter(Boolean);
      if (lista.includes(nombre)) return;
      lista.push(nombre);
      const data = await this._pide('/cart/update.js', { attributes: { [clave]: lista.join(', ') } }, false);
      if (data && data.attributes) {
        if (this._base) this._base.attributes = data.attributes;
        if (this.cart) this.cart.attributes = data.attributes;
      }
    } catch (e) {
      /* solo es para medir: nunca estorba a la compra */
    }
  },

  /**
   * `conSecciones` existe porque una reconciliación puede llevar dos peticiones (primero
   * quitar, luego añadir) y renderizar las secciones en la primera es trabajo tirado: ese
   * HTML nace ya caducado y se pinta el de la segunda. Solo lo pide la última.
   */
  async _pide(url, cuerpo, conSecciones) {
    const body = Object.assign(
      conSecciones === false
        ? {}
        : { sections: seccionesVivas().join(','), sections_url: window.location.pathname },
      cuerpo
    );
    // Tope de 15 s. Las peticiones del carrito salen por una cola EN SERIE, así que una
    // sola que se quede colgada (red mala, móvil que pierde cobertura a mitad) dejaría
    // la cola parada para siempre: a partir de ahí el cliente seguiría tocando, la
    // pantalla seguiría haciéndole caso y NADA volvería a llegar a Shopify. Con el tope,
    // esa petición muere, salta al catch, el carrito se vuelve a leer y la cola sigue.
    const corta = new AbortController();
    const reloj = setTimeout(() => corta.abort(), 15000);
    let r;
    try {
      r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: corta.signal,
      });
    } finally {
      clearTimeout(reloj);
    }
    const data = await r.json();
    if (!r.ok) throw data;
    return data;
  },

  /**
   * LAS BAJAS NO SE OLVIDAN. Este trío es la respuesta a «he borrado un Bubu y ha
   * vuelto».
   *
   * Hasta ahora, cuando una petición fallaba (red mala, un túnel, el móvil que cambia
   * de wifi a datos, los 15 s del tope), el catch hacía tabla rasa: se tiraban TODAS
   * las intenciones que iban en ese viaje y se volvía a leer el carrito de Shopify.
   * Para un alta eso está bien —más vale no enseñar algo que no llegó a entrar—, pero
   * para una BAJA es exactamente el fallo que se reporta: el cliente borra, la pantalla
   * le dice que sí, la petición se pierde, y al volver a leer el carrito la línea
   * reaparece como si no hubiera pasado nada. Y no hay forma de saber que ha pasado.
   *
   * La asimetría es deliberada: las altas fallidas se olvidan, las bajas se REHACEN.
   *
   * Y se rehacen en absoluto, nunca en relativo. Reintentar el «quita uno» tal cual era
   * la trampa evidente: si la petición sí había llegado y lo que se perdió fue la
   * respuesta, volver a restar uno quitaría DOS. Por eso, al fallar, se mira lo que la
   * pantalla está enseñando (el deseo), se relee el carrito de verdad, y solo si el
   * servidor tiene MÁS de lo que el cliente quiere se apunta un «esta línea vale N»
   * sobre keys que existen de verdad. Si la baja sí se aplicó, el servidor ya tiene lo
   * que toca y no se apunta nada: rehacerlo es idempotente por construcción.
   *
   * Solo se tocan los grupos que el cliente acaba de rebajar. Sin esa cerca, un carrito
   * modificado en otra pestaña se vería como «sobra» y esto lo borraría.
   */
  _gruposRebajados() {
    const g = new Set();
    this._ops.forEach((o) => {
      if (o.t === 'menos') { g.add(this._grupo(o.vid, o.props)); return; }
      if (o.t !== 'set') return;
      if (o.grupo) { g.add(o.grupo); return; }
      // Intenciones antiguas sin grupo: la key se resuelve contra el _base VIEJO.
      const l = this._base && this._base.items.find((x) => x.key === o.key);
      if (l) g.add(this._grupo(l.variant_id, l.properties));
    });
    return g;
  },

  /** Lo que la pantalla está enseñando ahora mismo, por grupo. Los regalos no cuentan. */
  _deseoPorGrupo() {
    const m = new Map();
    if (!this.cart) return m;
    this.cart.items.forEach((i) => {
      if (esRegalo(i)) return;
      const g = this._grupo(i.variant_id, i.properties);
      m.set(g, (m.get(g) || 0) + i.quantity);
    });
    return m;
  },

  _bajasFallidas: 0,

  /**
   * Con el carrito recién releído, vuelve a apuntar las bajas que el servidor no llegó
   * a aplicar. Devuelve true si ha tenido que apuntar alguna.
   */
  _reaplicaBajas(rebajados, deseo) {
    if (!this._base || !rebajados.size) return false;
    /* Fusible. Si la MISMA baja no entra cuatro veces seguidas, algo pasa que no se
       arregla insistiendo (una línea que Shopify no deja tocar, un carrito bloqueado).
       Antes que martillear al servidor para siempre, se deja de rehacer y se avisa: la
       pantalla dirá la verdad, aunque la verdad sea que la línea sigue ahí. */
    if (this._bajasFallidas > 3) return false;
    const porGrupo = new Map();
    this._base.items.forEach((i) => {
      if (esRegalo(i)) return;
      const g = this._grupo(i.variant_id, i.properties);
      const e = porGrupo.get(g) || { qty: 0, lineas: [] };
      e.qty += i.quantity;
      e.lineas.push(i);
      porGrupo.set(g, e);
    });
    let rehecho = false;
    porGrupo.forEach((e, g) => {
      if (!rebajados.has(g)) return;
      const quiere = deseo.has(g) ? deseo.get(g) : 0;
      if (quiere >= e.qty) return;
      let sobra = e.qty - quiere;
      for (let i = e.lineas.length - 1; i >= 0 && sobra > 0; i--) {
        const l = e.lineas[i];
        const baja = Math.min(l.quantity, sobra);
        this._apunta({ t: 'set', key: l.key, qty: l.quantity - baja, grupo: g });
        sobra -= baja;
        rehecho = true;
      }
    });
    if (rehecho) this._bajasFallidas++;
    return rehecho;
  },

  /**
   * Intenciones muertas. Un «esta línea vale N» apunta a una key concreta; si esa key
   * ya no está en el carrito del servidor, la línea se fue y la intención está
   * cumplida. Dejarla puesta no cambiaba la pantalla (_ponSet no encuentra nada y no
   * hace nada), pero sí dejaba hayPendientes() diciendo que sí para siempre: al salir
   * de la página o al cambiar de pestaña se intentaba vaciar una cola que ya no tenía
   * nada que mandar.
   */
  _limpiaOps() {
    if (!this._base) return;
    /* AQUÍ ESTABA EL «LO BORRO RÁPIDO Y VUELVE». Esta limpieza tiraba toda intención
       «esta línea vale N» cuya key ya no estuviera en el carrito, dando por hecho que
       la línea se había ido. Pero la key de una línea CAMBIA sola cuando el carrito
       cruza un tramo de la escalera (ver grupoDom): borras A, y mientras viaja esa
       petición borras B; llega la respuesta de A con B renombrada; la baja de B —que
       aún no había salido porque la cola va en serie— apuntaba a la key vieja, no la
       encontraba aquí y se tiraba como si estuviera cumplida. B volvía a la pantalla y
       se quedaba en el carrito. Con calma no pasaba porque cada baja salía sola.
       Una intención solo está muerta si NI su key NI su grupo siguen en el carrito. */
    const vivas = new Set(this._base.items.map((i) => i.key));
    const grupos = new Set(this._base.items.map((i) => this._grupo(i.variant_id, i.properties)));
    this._ops = this._ops.filter((o) => o.t !== 'set' || vivas.has(o.key) || (o.grupo && grupos.has(o.grupo)));
  },

  /** ¿Queda algo que el cliente ha pedido y Shopify todavía no sabe? */
  hayPendientes() {
    return this._ops.some((o) => !o.enviada);
  },

  /**
   * Manda YA lo que haya pendiente y espera a que termine. Si no hay nada, vuelve
   * enseguida y no toca la red.
   */
  async vacia() {
    if (!this._base) return;
    clearTimeout(this._t);
    await conBloqueo(() => this._concilia());
  },

  async _concilia() {
    if (!this._base || this._enCurso) return;
    const plan = this._plan();
    const hayUpd = Object.keys(plan.updates).length > 0;
    const marcadas = this._ops.filter((o) => !o.enviada);
    if (!hayUpd && !plan.add.length) {
      this._ops = this._ops.filter((o) => !marcadas.includes(o));
      // Puede que la vuelta anterior se saltara el pintado porque el cliente seguía
      // tocando y esta ya no tenga nada que mandar. Entonces la pantalla lleva razón en
      // los números pero le falta el HTML del servidor: se pide una vez y se acabó.
      if (this._debePintar) {
        this._debePintar = false;
        this._enCurso = true;
        try {
          await this._refresca();
        } finally {
          this._enCurso = false;
        }
      }
      // Y si mientras se esperaba a ese repintado el cliente ha vuelto a tocar, esas
      // intenciones son NUEVAS: no estaban en `marcadas`, no se han mandado y esta
      // salida temprana se saltaba el reenganche del final de la función. Ahí se
      // quedaban, calladas y para siempre.
      if (this._ops.some((o) => !o.enviada)) this._programa(0);
      return;
    }
    marcadas.forEach((o) => (o.enviada = true));
    this._enCurso = true;
    try {
      let data = null;
      let carrito = null;
      // Primero se quita y después se añade: así una caja que se vacía y se vuelve a
      // llenar nunca pasa por un estado intermedio raro.
      if (hayUpd) {
        data = await this._pide('/cart/update.js', { updates: plan.updates }, !plan.add.length);
        // /cart/update.js YA devuelve el carrito entero. Pedirlo otra vez con /cart.js
        // era un viaje de ida y vuelta de más en cada toque, y el borrado rápido son
        // todo updates: es justo donde más se notaba el retraso.
        carrito = data;
      }
      if (plan.add.length) {
        // /cart/add.js solo devuelve lo añadido, así que ahí sí hay que pedir el carrito.
        data = await this._pide('/cart/add.js', { items: plan.add }, true);
        carrito = null;
      }
      // Orden de preferencia, de gratis a caro: el que viene dentro del HTML repintado,
      // el que devuelve /cart/update.js, y solo si no hay ninguno, un viaje a /cart.js.
      const deSecciones = this._deSecciones(data && data.sections);
      this._base = deSecciones || (carrito && Array.isArray(carrito.items) ? carrito : await this._trae());
      this._ops = this._ops.filter((o) => !o.enviada);
      this._limpiaOps();
      Canjes.vigila(this._base);
      // El viaje ha ido bien: el fusible de las bajas vuelve a estar entero.
      this._bajasFallidas = 0;
      this._deriva();
      // «anadidos» viaja con el repintado porque hay dos animaciones colgadas de él y no
      // son adornos: el CTA de la ficha pasa a «Añadido al carrito» (bubu-producto.js) y
      // el hueco recién llenado de «Monta tu caja» hace su pop y se deja ver
      // (bubu-tienda.js). Los regalos quedan fuera: el activador no es algo que el
      // cliente haya pulsado y no tiene que celebrarse.
      const anadidos = plan.add
        .filter((a) => !(a.properties && a.properties._regalo))
        .map((a) => Number(a.id));
      const detalle = { sincronizado: true };
      if (anadidos.length) detalle.anadidos = anadidos;
      // Si mientras viajaba la petición el cliente ha seguido tocando, el HTML que acaba
      // de llegar YA no dice la verdad: describe el carrito de hace un momento. Pintarlo
      // es meter filas que hay que volver a quitar acto seguido. Como al salir de aquí se
      // dispara otra reconciliación inmediata, se deja la pantalla como está (el modelo ya
      // la tiene al día) y se pinta con el HTML bueno, el de la próxima respuesta.
      const quedaIntencion = this._ops.some((o) => !o.enviada);
      if (quedaIntencion) this._debePintar = true;
      this._repinta(quedaIntencion ? null : data && data.sections, detalle);
    } catch (e) {
      // Lo que el servidor no ha podido hacer se olvida: más vale que la pantalla diga
      // la verdad a que se quede enseñando para siempre algo que no llegó a pasar.
      // CON UNA EXCEPCIÓN: las bajas. Ver _reaplicaBajas — un borrado que se pierde en
      // la red no puede resucitar la línea. Se apunta ANTES de tirar las intenciones,
      // porque es de ellas de donde se sabe qué grupos había tocado el cliente, y se
      // rehace DESPUÉS de releer el carrito, ya en absoluto y sobre keys de verdad.
      const rebajados = this._gruposRebajados();
      const deseo = this._deseoPorGrupo();
      this._ops = this._ops.filter((o) => !o.enviada);
      try {
        this._base = await this._trae();
      } catch (_) {
        /* se queda con el último bueno */
      }
      this._limpiaOps();
      const rehecho = this._reaplicaBajas(rebajados, deseo);
      this._deriva();
      await this._refresca();
      // Una baja rehecha tiene que salir YA, no esperar al vigilante: es lo único que
      // separa la pantalla de volver a enseñar lo que el cliente acaba de quitar.
      if (rehecho) this._programa(0);
      // Un fallo de red que se va a reintentar solo no merece un cartel rojo: el cliente
      // ya ve que su borrado sigue hecho. Solo se avisa si se ha agotado el fusible.
      if (!esRuidoDeCarrito(e) && !rehecho) this._error(e);
    } finally {
      this._enCurso = false;
    }
    if (this._ops.some((o) => !o.enviada)) this._programa(0);
  },

  _debePintar: false,

  /** Vuelve a pedir las secciones para que el HTML del servidor y el modelo coincidan. */
  async _refresca() {
    try {
      const s = await fetch(`${window.location.pathname}?sections=${seccionesVivas().join(",")}`, {
        headers: { Accept: 'application/json' },
      });
      const secciones = await s.json();
      const deSecciones = this._deSecciones(secciones);
      if (deSecciones) {
        this._base = deSecciones;
        this._ops = this._ops.filter((o) => !o.enviada);
        this._limpiaOps();
        this._deriva();
      }
      this._repinta(secciones, { sincronizado: true });
    } catch (_) {
      this._pintaRapido();
    }
  },

  /** Comprueba si falta algo por mandar (el activador del primer pedido, por ejemplo). */
  _revisa() {
    if (!this._base) return;
    const plan = this._plan();
    if (plan.add.length || Object.keys(plan.updates).length) this._programa(0);
  },

  /**
   * EL VIGILANTE. La red de seguridad de todo lo de arriba.
   *
   * Por qué existe, con el caso medido delante: añadiendo seis Bubus a 60 ms y
   * empezando a borrarlos a los 200 ms —antes de que el servidor conteste ni al
   * primero— se llegaba a este estado y se quedaba ahí:
   *
   *     pantalla: 1 Bubu   ·   Shopify: 0   ·   1 intención sin mandar   ·   sin reloj
   *
   * Dieciocho segundos después seguía igual. Y eso es exactamente lo que cuentan los
   * betatesters: borras algo, la pantalla dice que sí, y la verdad es otra. A partir de
   * ahí todo lo que venga después hereda la mentira: el contador dice tres y la caja
   * está vacía, o un Bubu «vuelve» cuando algo fuerza una lectura del carrito.
   *
   * La reconciliación se programa desde muchos sitios (cada clic, el final de cada
   * viaje, al volver a la pestaña). Buscar la carrera concreta que deja una intención
   * huérfana es perseguir un fantasma: mañana aparece otra. Lo que NO puede pasar es
   * que el desacuerdo sobreviva. Así que cada segundo y medio se hace una pregunta
   * tonta y barata —¿lo que pinto y lo que hay en Shopify es lo mismo?— y, si no lo es
   * y no hay nada en marcha, se manda la diferencia. Fin del fantasma.
   *
   * Cuesta dos Map sobre un puñado de líneas; no toca la red salvo que de verdad haya
   * algo que mandar. Y no se convierte en un bucle: si la MISMA diferencia no se
   * resuelve cuatro veces seguidas (una variante agotada, un regalo que Shopify no deja
   * añadir), se deja estar en vez de martillear al servidor para siempre.
   */
  _insiste: 0,
  _firma: '',
  _vigila() {
    if (!this._base || this._enCurso) return;
    const plan = this._plan();
    if (!plan.add.length && !Object.keys(plan.updates).length) {
      this._insiste = 0;
      this._firma = '';
      return;
    }
    const firma = JSON.stringify(plan);
    if (firma === this._firma) {
      if (++this._insiste > 3) return;
    } else {
      this._firma = firma;
      this._insiste = 1;
    }
    this._programa(0);
  },

  // ---------- API pública (la usan bubu-cumples.js y los custom elements) ----------
  async add(items, opts = {}) {
    if (!this._base) await conBloqueo(() => this.get());
    this.pideAnadir(items, opts.origen);
    await this._ahora();
    return this.cart;
  },

  async change(key, quantity) {
    this.pideCambio(key, quantity);
    await this._ahora();
    return this.cart;
  },

  async update(updates) {
    Object.keys(updates).forEach((k) => this.pideCambio(k, updates[k]));
    await this._ahora();
    return this.cart;
  },

  async clear() {
    return conBloqueo(async () => {
      this._ops.length = 0;
      await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
      this._base = await this._trae();
      this._deriva();
      await this._refresca();
    });
  },

  /** Cantidad total de un producto (por id de producto) en el carrito, sin regalos. */
  qtyProducto(productId) {
    if (!this.cart) return 0;
    return this.cart.items.filter((i) => i.product_id === Number(productId) && !esRegalo(i) && !esPack(i)).reduce((a, i) => a + i.quantity, 0);
  },
  qtyVariante(variantId) {
    if (!this.cart) return 0;
    return this.cart.items.filter((i) => i.variant_id === Number(variantId) && !esRegalo(i) && !esPack(i)).reduce((a, i) => a + i.quantity, 0);
  },
  /** Línea (key) de una variante sin propiedades especiales. */
  lineaVariante(variantId) {
    if (!this.cart) return null;
    return this.cart.items.find((i) => i.variant_id === Number(variantId) && !esRegalo(i) && !esPack(i)) || null;
  },
  linea(key) {
    return this.cart ? this.cart.items.find((i) => i.key === key) || null : null;
  },

  /**
   * El activador ya no es una petición aparte: vive en _activadorEnModelo y sale en el
   * mismo viaje que todo lo demás. Esto se queda por compatibilidad con quien lo llame.
   */
  async _activador() {
    this._revisa();
  },

  // ---------- Apertura automática tras añadir ----------
  // La regla vale para tienda y para ficha, y la hoja se abre en DOS momentos, con un
  // motivo cada uno (21/09/2026):
  //   · al pasar de 0 a 1: orientar. Es el momento de enseñar dónde aterriza lo que
  //     añades y qué barra empieza a crecer, y por eso se enseña entero.
  //   · en el añadido que gana el activador de regalo (normalmente el segundo Bubu):
  //     premiar. Si la hoja no se abre ahí, el aviso del regalo vuela hacia el icono
  //     del carrito y la clienta no ve el activador entrar en su caja; abriéndola, el
  //     regalo se ve, no solo se anuncia.
  // El resto de añadidos no interrumpen la compra: el producto vuela hasta la caja (o
  // el carrito) y la barra de hitos da un golpe. Es el mismo mensaje sin sacar a nadie
  // de la tienda, que es lo que hace falta para que compren de seguido sabiendo en todo
  // momento que su caja está ahí llenándose.
  _trasAnadir(abrir, origen, tClic) {
    const enTienda = CFG.plantilla === 'collection';
    const dentroCesta = origen && origen.closest && origen.closest('#bubu-cesta');
    clearTimeout(this._tAbrir);
    if (dentroCesta) return;
    // El vuelo va SIEMPRE, desde el primer artículo y en las dos superficies: en la
    // tienda la miniatura viaja a «Monta tu caja» y en la ficha al carrito. Es la misma
    // animación, el mismo recorrido y el mismo golpe al aterrizar, así que se aprende
    // una vez y ya vale para toda la web.
    // El vuelo va dentro de un try: si por lo que sea falla (una foto que no está, un
    // destino que se mueve), NO puede llevarse por delante la apertura del carrito, que
    // es lo único imprescindible del primer producto.
    try {
      this._vuela(origen);
    } catch (_) {
      /* el vuelo es un adorno; la caja abriéndose no */
    }
    if (!abrir) return;
    // La hoja se abre cuando el vuelo aterriza, no encima de él: primero se ve a dónde
    // va el Bubu, después se abre para enseñar por dentro lo que se acaba de montar.
    const resto = (ms) => (tClic ? Math.max(0, ms - (performance.now() - tClic)) : ms);
    this._tAbrir = setTimeout(() => {
      if (enTienda) this.abrirCaja(origen);
      else this.abrirCesta(origen);
      setTimeout(() => {
        const d = this._destino();
        if (d) this._bump(d);
      }, 380);
    }, resto(640));
  },

  abrirCesta(origen) {
    const hoja = document.getElementById('bubu-hoja-caja');
    if (hoja && hoja.abierta) hoja.cerrar();
    const cesta = document.getElementById('bubu-cesta');
    if (cesta && typeof cesta.abrir === 'function') cesta.abrir(origen);
    else window.location.href = '/cart';
  },
  abrirCaja(origen) {
    const hoja = document.getElementById('bubu-hoja-caja');
    if (hoja && esMovil() && typeof hoja.abrir === 'function') {
      hoja.abrir(origen);
      return;
    }
    // En PC la tarjeta de «Monta tu caja» ya está en pantalla: abrir el cajón encima
    // taparía la tienda sin enseñar nada nuevo. Se le enciende el anillo a la tarjeta,
    // que es la misma señal de «ha ido aquí» sin robar la pantalla.
    const tarjeta = document.querySelector('[data-bubu-caja]');
    if (tarjeta && tarjeta.getClientRects().length) {
      this._bump(tarjeta.querySelector('[data-bubu-escalera]') || tarjeta);
      return;
    }
    this.abrirCesta(origen);
  },

  // ---------- El producto vuela a su destino ----------
  /** Dónde aterriza lo que se añade, por orden de cercanía a lo que el cliente mira. */
  _destino() {
    const dentro = (el) => {
      if (!el || !el.getClientRects().length) return false;
      const r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < (window.innerHeight || 0);
    };
    const primero = (...sel) => {
      for (const q of sel) {
        const el = document.querySelector(q);
        if (dentro(el)) return el;
      }
      return null;
    };

    // 1. Con una hoja YA abierta, el destino es su propia barra de hitos: está delante
    //    de los ojos y es justo lo que va a crecer.
    const abierta = primero(
      '.bubu-hoja--caja:not([hidden]) [data-bubu-escalera]',
      '#bubu-cesta:not([hidden]) [data-bubu-escalera]'
    );
    if (abierta) return abierta;

    // 2. En la TIENDA lo que se llena es la caja: la tarjeta pegada en PC o la barra de
    //    hitos fija de abajo en móvil. Es donde el cliente ve su pedido montarse.
    const caja = primero('[data-bubu-caja] [data-bubu-escalera]', '.bubu-barra-movil [data-bubu-escalera]');
    if (CFG.plantilla === 'collection' && caja) return caja;

    // 3. En cualquier otro sitio —y la ficha es el caso importante— el destino es el
    //    ICONO DEL CARRITO de la cabecera. El carrito vive arriba y desde arriba se
    //    abre, así que el recorrido cuenta la historia correcta: el producto sube al
    //    carrito. Antes aquí ganaba la escalera espejo del bloque de compra, que está
    //    POR DEBAJO del botón de añadir: el Bubu salía hacia abajo, justo al revés de
    //    lo que pasa después.
    const icono = primero('.bubu-cab__icono[data-bubu-abrir-cesta]', '.bubu-cab__carrito');
    if (icono) return icono;

    // 4. Último recurso: cualquier escalera a la vista.
    return caja || primero('[data-bubu-esc-espejo-in] [data-bubu-escalera]');
  },

  /** Respingo del destino al recibir el producto: lleva la mirada a la barra. */
  _bump(el) {
    if (!el) return;
    el.classList.remove('is-sumando');
    void el.offsetWidth;
    el.classList.add('is-sumando');
    clearTimeout(el._bubuSuma);
    el._bubuSuma = setTimeout(() => el.classList.remove('is-sumando'), 760);
  },

  /**
   * Miniatura del producto viajando desde la tarjeta hasta la barra de hitos. El
   * recorrido se mide en el momento con getBoundingClientRect de los dos extremos, así
   * que acierta en 1440 y en 390 sin porcentajes inventados, y si no hay destino a la
   * vista no hace nada en vez de volar a ninguna parte.
   */
  _vuela(origen) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!origen) return;
    const hasta = this._destino();
    if (!hasta) return;
    // De dónde sale la miniatura, por orden: la tarjeta pulsada, la foto grande de la
    // ficha (el slide que se está viendo), la barra fija de la ficha. Si el botón no
    // vive dentro de ninguna (la barra sticky del móvil, por ejemplo), se busca la
    // ficha de la página. Y si aun así no hay foto, vuela una ficha lisa de marca: el
    // cliente tiene que ver SIEMPRE algo salir hacia su caja.
    const cuna =
      (origen.closest &&
        origen.closest('[data-bubu-tarjeta], [data-bubu-ficha], [data-bubu-pack], .bubu-pack-tarjeta, .bubu-mini, .bubu-ficha-barra, .bubu-extra')) ||
      document.querySelector('[data-bubu-ficha]');
    let img = null;
    if (cuna) img = cuna.querySelector('.bubu-ficha__slide.is-activa img') || cuna.querySelector('img');
    const src =
      (img && (img.currentSrc || img.src)) ||
      (origen.dataset && origen.dataset.img) ||
      (cuna && cuna.dataset && cuna.dataset.img) ||
      '';
    const salida = img && img.getClientRects().length ? img : cuna && cuna.getClientRects().length ? cuna : origen;
    const a = salida.getBoundingClientRect();
    const b = hasta.getBoundingClientRect();
    if (!a.width || !b.width) return;
    const T = 44;
    const v = document.createElement('span');
    if (typeof v.animate !== 'function') return;
    v.className = 'bubu-vuelo' + (src ? '' : ' is-sin-foto');
    v.setAttribute('aria-hidden', 'true');
    if (src) v.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
    v.style.width = T + 'px';
    v.style.height = T + 'px';
    v.style.left = a.left + a.width / 2 - T / 2 + 'px';
    v.style.top = a.top + a.height / 2 - T / 2 + 'px';
    document.body.appendChild(v);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    // El arco sube un tercio del recorrido vertical (tope 56 px). En proporción al
    // viaje, así que en un salto corto no da un respingo raro y en uno largo se lee
    // como una parábola limpia en vez de un desplazamiento en línea recta.
    const arco = Math.min(56, Math.max(18, Math.abs(dy) * 0.34 + Math.abs(dx) * 0.06));
    const anim = v.animate(
      [
        { transform: 'translate(0px, 0px) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translate(' + dx * 0.5 + 'px, ' + (dy * 0.5 - arco) + 'px) scale(.82)', opacity: 1, offset: 0.5 },
        { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(.28)', opacity: 0, offset: 1 },
      ],
      { duration: 560, easing: 'cubic-bezier(.34,.02,.24,1)', fill: 'forwards' }
    );
    const fin = () => {
      clearTimeout(v._bubuT);
      v.remove();
    };
    anim.onfinish = () => {
      fin();
      this._bump(hasta);
    };
    v._bubuT = setTimeout(fin, 900);
  },

  /**
   * EL TABLERO NO SE MUEVE. LOS BUBUS SÍ.
   *
   * Son dos cosas distintas y hay que separarlas bien, porque confundirlas fue el error:
   *
   *   · EL TABLERO —los cuadrados de puntos con el «+»— es el escenario y se queda
   *     QUIETO. Mide filas enteras: tres de tres en la tarjeta de PC (nueve casillas) y
   *     dos de cuatro en la hoja móvil (ocho), siempre con una casilla libre de margen.
   *     Solo crece cuando de verdad se llena, y nunca encoge por debajo de ese mínimo.
   *     Antes se contaban «6 huecos más los que hicieran falta», así que cambiaba de
   *     tamaño con CADA Bubu (9 con la caja vacía, 7 al meter el primero, y de ahí
   *     subiendo de una en una) y la última fila se recomponía en cada toque.
   *
   *   · LOS BUBUS SÍ SE MUEVEN, y tienen que hacerlo: van todos juntos y lo más arriba
   *     posible. Si quitas el del medio, los de detrás SE RECOGEN y cierran el hueco
   *     deslizándose (lo anima reflujo). Un hueco a medias entre dos Bubus no es un
   *     escenario quieto, es una caja rota.
   *
   * Los regalos van SIEMPRE los primeros, delante de todo lo demás. Un regalo abre la
   * caja; no puede quedarse en mitad de la rejilla según el orden en que Shopify
   * devuelva las líneas.
   *
   * Por qué hace falta `data-celda` → `order` y no basta el orden del DOM: el DOM se
   * desordena solo. El hueco optimista se pinta sustituyendo a un hueco cualquiera, el
   * repintado del servidor reordena por su cuenta y un Bubu que se está yendo sigue
   * ocupando su sitio hasta que acaba su animación. Escribiendo aquí la posición de
   * cada uno, lo que se ve no depende de en qué orden haya quedado el HTML.
   */
  _ajustaHuecos() {
    document.querySelectorAll('[data-bubu-slots]').forEach((cont) => {
      // Regalos primero, después el resto, y cada uno en la casilla que le toca contando
      // desde arriba. Un Bubu que se está yendo sigue en la lista hasta que termina de
      // irse: así no se recoge nadie hasta que de verdad se ha ido, y el hueco se cierra
      // de una sola vez en vez de en dos tirones.
      const puestos = Array.from(cont.children).filter((n) => n.classList && n.classList.contains('is-lleno'));
      const orden = puestos
        .filter((n) => n.classList.contains('is-regalo'))
        .concat(puestos.filter((n) => !n.classList.contains('is-regalo')));
      orden.forEach((n, i) => {
        n.dataset.celda = String(i);
        if (n.style) n.style.order = String(i);
      });
      // Tamaño del tablero: filas enteras y una casilla libre de margen.
      const necesarias = orden.length + 1;
      const celdasPc = Math.max(3, Math.ceil(necesarias / 3)) * 3;
      const celdasMov = Math.max(2, Math.ceil(necesarias / 4)) * 4;
      const total = Math.max(celdasPc, celdasMov);
      // Los huecos rellenan lo que quede, siempre por detrás de los Bubus.
      const faltan = Math.max(0, total - orden.length);
      let vacios = Array.from(cont.querySelectorAll('.bubu-slot.is-vacio'));
      for (let i = vacios.length; i > faltan; i--) vacios[i - 1].remove();
      for (let i = vacios.length; i < faltan; i++) cont.appendChild(nodoHueco());
      vacios = Array.from(cont.querySelectorAll('.bubu-slot.is-vacio'));
      vacios.forEach((el, i) => {
        const c = orden.length + i;
        el.dataset.celda = String(c);
        el.style.order = String(c);
        // Las casillas que solo existen en uno de los dos tableros se esconden en el
        // otro (bubu-caja.css). El tablero de móvil y el de PC no miden lo mismo.
        el.classList.toggle('is-solo-pc', c >= celdasMov);
        el.classList.toggle('is-solo-movil', c >= celdasPc);
      });
    });
  },

  // ---------- Pintura optimista ----------
  // Shopify tarda ~0,7 s en responder. En vez de esperarle, el cambio se pinta en el
  // acto desde el modelo derivado (cantidades, huecos de la caja, escalera, contadores
  // y totales) y la respuesta del servidor solo lo confirma.

  /** Datos de la tarjeta/botón desde el que se ha añadido, para pintar la línea ya. */
  _pistas(origen) {
    if (!origen || !origen.closest) return {};
    // OJO: si el botón no vive dentro de ninguna de estas cunas, NO se busca una foto
    // por la página. Antes se caía a `document` y `document.querySelector('img')`
    // devolvía la primera imagen del documento, que es el LOGO de la cabecera: al
    // añadir un pack —cuya tarjeta no estaba en esta lista— los huecos de «Lo que
    // llevas» se llenaban durante un segundo de logos negros gigantes. Sin foto se
    // pinta el hueco liso y la buena entra cuando contesta Shopify; eso no se nota.
    // Si se añade un tipo de tarjeta nuevo, su selector va AQUÍ.
    const t = origen.closest('[data-bubu-tarjeta], [data-bubu-ficha], [data-bubu-pack], .bubu-pack-tarjeta, .bubu-mini, .bubu-extra, .bubu-ficha-barra');
    if (!t) return {};
    const img = t.querySelector('img');
    const nombre = t.querySelector('[data-bubu-nombre], .bubu-mini__nombre, .bubu-tarjeta__nombre, .bubu-ficha__titulo, .bubu-extra__nombre');
    // Las tiras de extras/upsell enseñan la foto «a juego» (bubu.foto_upsell), pero la
    // línea del carrito y la casilla de la caja llevan la foto normal del producto. El
    // tile trae esa foto normal en data-bubu-foto; si no, se usa la que se ve.
    const src = t.dataset.bubuFoto || (img ? img.currentSrc || img.src : '');
    // Los dos anchos que pinta el servidor: 200 en «Monta tu caja», 120 en el carrito.
    // Se piden ya, en el mismo milisegundo del clic.
    const caja = anchoImg(src, 200);
    const cesta = anchoImg(src, 120);
    precarga(caja);
    precarga(cesta);
    // El precio de la unidad (data-precio, en céntimos) viaja con la pista: la línea
    // provisional se pinta con el precio DE ESE producto y no con el base de 13,95, así
    // que un Mystery (10,95), una Tarta (16,95) o un activador (4,95) mueven el total y
    // la barra de «Te faltan…» a su sitio desde el clic, sin el salto de medio segundo.
    return { img: src, imgCaja: caja, imgCesta: cesta, titulo: nombre ? nombre.textContent.trim() : '', precio: Number(t.dataset.precio) || 0 };
  },

  _tramosVistos: [false, false, false],
  _escaleraLocal() {
    const escs = document.querySelectorAll('[data-bubu-escalera]');
    if (!escs.length || !this.cart) return;
    const E = CFG.escalera || [{ uds: 3, pct: 10 }, { uds: 4, pct: 0 }, { uds: 5, pct: 15 }];
    const u1 = E[0].uds, u2 = E[1].uds, u3 = E[2].uds, p1 = E[0].pct, p3 = E[2].pct;
    const umbral = Number(CFG.envioUmbral || 0);
    let pack = false;
    this.cart.items.forEach((i) => { if (esPack(i)) pack = true; });
    const uds = this._uds();
    // Con un pack de evento y NADA que sume para los tramos, la escalera no aplica y se
    // esconde entera. Misma cuenta que snippets/bubu-escalera.liquid: lo que la trae de
    // vuelta es algo que SUMA —un slime, un Mystery, una Tarta—, no un accesorio
    // «no-cuenta» ni un lote cerrado, que dejarían la barra diciendo «Añade 3 Bubus»
    // sobre una barra igual de vacía. Se hace aquí y no solo en el servidor para que
    // aparezca y desaparezca en el mismo instante del clic.
    const soloPack = pack && uds === 0;
    const total = this.cart.total_price;
    /* EL ENVÍO GRATIS SE MIDE EN EUROS, NO EN BUBUS. Misma regla que
       snippets/bubu-escalera.liquid: antes bastaban 4 unidades O el importe, y cuatro
       Bubus con el −10 % del primer hito salían por 50,22 € — cumplían los 50 € por
       veintidós céntimos. Un código de descuento o una caja con algo más barato y la
       barra celebraba un envío gratis que el checkout cobraba. Ahora la barra mira lo
       mismo que va a mirar Shopify. `u2` solo entra si el umbral está a cero. */
    const m = [uds >= u1, umbral > 0 ? total >= umbral || pack : uds >= u2 || pack, uds >= u3];
    let sig = -1;
    if (!m[0]) sig = 0; else if (!m[1]) sig = 1; else if (!m[2]) sig = 2;
    const pos = [30, 62, 94];
    // La barra parte del último hito conseguido SIN saltarse ninguno: si el envío gratis
    // ya está hecho (pack de cumple) pero faltan los 3 Bubus, la barra no puede aparecer
    // llena por encima de un hito vacío. Misma regla que snippets/bubu-escalera.liquid.
    let basepos = 0;
    for (let i = 0; i < m.length; i++) { if (!m[i]) break; basepos = pos[i]; }
    let pct = 100;
    if (sig >= 0) {
      let frac, hasta;
      if (sig === 0) { frac = u1 ? uds / u1 : 0; hasta = pos[0]; }
      else if (sig === 1) {
        // La barra de este tramo avanza con el MISMO número que decide el hito: el
        // total del carrito contra el umbral. Antes mezclaba unidades e importe y se
        // adelantaba al hito.
        frac = umbral ? total / umbral : u2 ? uds / u2 : 0;
        hasta = pos[1];
      }
      else { frac = (uds - u1) / Math.max(1, u3 - u1); hasta = pos[2]; }
      frac = Math.max(0, Math.min(1, frac));
      const desde = basepos === 0 ? 4.5 : basepos;
      if (hasta < desde) hasta = desde;
      pct = Math.round(((hasta - desde) * frac + desde) * 10) / 10;
    }
    /* SEIS ESTADOS, UNO POR ESCALÓN (21/09/2026). Cada mensaje pide UNA sola acción y
       nombra el premio que está a UNA unidad de distancia. Nunca «añade 2 más»: si
       faltan dos escalones, se nombra el de al lado y ya se verá el siguiente.
       Los dos primeros son nuevos: antes, con uno o dos Bubus, la barra hablaba del
       −10 % que estaba a dos unidades y el cliente no tenía ningún motivo para coger el
       segundo. Ahora el segundo trae el activador, que es el premio que se le acababa de
       quitar al subir el descuento a tres.
       El emoji va DENTRO del texto fuerte, no como icono aparte, para que si algún día
       no cupiera se lo llevase el recorte entero en vez de dejar medio glifo colgando.
       Anchos medidos a 390 px con Inter cargada y sin la píldora «Tu caja» (que se
       esconde en móvil): el estado más justo deja 50 px de margen, un 15 %. Ese colchón
       está puesto a propósito, por si Inter tarda en cargar y el navegador pinta con la
       fuente del sistema, que es más ancha. */
    const EMO = { act0: '🤩', act1: '🔥', actOk: '✨', envio: '💜', pct: '🚀', tope: '⭐️' };
    const minAct = Math.max(1, Number(CFG.activadorMin || 1));
    let fuerte = '', suave = '';
    if (uds < minAct) {
      fuerte = uds === 0
        ? `${EMO.act0} Añade ${minAct} ${minAct === 1 ? 'Bubu' : 'Bubus'}`
        : `${EMO.act1} Añade ${minAct - uds} más`;
      suave = ' y te regalamos el activador';
    } else if (uds === minAct && sig === 0 && u1 > uds) {
      // El único estado que celebra: acaba de conquistar el activador. El premio ganado
      // va en negrita y el siguiente paso, justo detrás.
      fuerte = `${EMO.actOk} Activador gratis`;
      suave = ` · ${u1 - uds} más y ahorras un ${p1} %`;
      // A una unidad del −10 %, en euros: «el tercero por 9,78 €». Con un pack de cumple
      // dentro no: Shopify aplica solo el mayor de los dos descuentos y el número no
      // sería exacto, así que se queda el texto de siempre.
      const sig1 = cuestaSiguiente(this.cart.items, p1);
      if (u1 - uds === 1 && !pack && ORDINAL[u1] && sig1 > 0) suave = ` · el ${ORDINAL[u1]} por ${money(sig1)}`;
    } else if (sig === 0) {
      fuerte = uds === 0 ? `${EMO.act0} Añade ${u1} Bubus` : `${EMO.act1} Añade ${u1 - uds} más`;
      suave = ` y ahorras un ${p1} %`;
    } else if (sig === 1) {
      // En euros, no en Bubus: es lo único que el checkout va a mirar, y vale igual
      // para una caja de cuatro Bubus que para una con un pack y un accesorio.
      fuerte = umbral > 0
        ? `${EMO.envio} Te faltan ${money(Math.max(1, umbral - total))}`
        : `${EMO.envio} Añade ${Math.max(1, u2 - uds)} más`;
      suave = ' y el envío te sale gratis';
    } else if (sig === 2) {
      fuerte = `${EMO.pct} Añade ${u3 - uds} más`;
      suave = ` y ahorras un ${p3} % en la caja`;
      // A una unidad del −15 %: «el quinto por 9,06 € · todo a −15 %».
      const sig3 = cuestaSiguiente(this.cart.items, p3);
      if (u3 - uds === 1 && !pack && ORDINAL[u3] && sig3 > 0) {
        fuerte = `${EMO.pct} El ${ORDINAL[u3]} por ${money(sig3)}`;
        suave = ` · todo a −${p3} %`;
      }
    } else {
      fuerte = `${EMO.tope} Lo tienes todo: ${p3} % y envío gratis`;
    }
    /* Aviso al pixel de Eventos del cliente cuando se CONQUISTA un tramo (no en cada
       repintado). Es la única forma de saber si la barra de hitos hace su trabajo:
       cuánta gente pasa de 2 a 3 Bubus y cuánta llega a 5. Shopify.analytics.publish
       es la puerta oficial al sandbox; si no está, no pasa nada. */
    const TRAMOS = ['descuento-1', 'envio-gratis', 'descuento-2'];
    m.forEach((hecho, i) => {
      if (!hecho || this._tramosVistos[i]) return;
      this._tramosVistos[i] = true;
      try {
        if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
          Shopify.analytics.publish('bubu_tramo', { tramo: TRAMOS[i], unidades: uds });
        }
      } catch (_) { /* el pixel nunca puede romper la barra */ }
    });

    escs.forEach((el) => {
      el.dataset.uds = uds;
      el.dataset.sig = sig;
      el.dataset.pct = pct;
      el.classList.toggle('is-completa', m[0] && m[1] && m[2]);
      el.classList.toggle('is-oculta', soloPack);
      const rel = el.querySelector('[data-bubu-esc-relleno]');
      if (rel) rel.style.width = pct + '%';
      const f = el.querySelector('.bubu-esc__fuerte');
      if (f) f.textContent = fuerte;
      let sv = el.querySelector('.bubu-esc__suave');
      if (!sv && suave && f) {
        sv = document.createElement('span');
        sv.className = 'bubu-esc__suave';
        f.after(sv);
      }
      if (sv) sv.textContent = suave;
      el.querySelectorAll('[data-bubu-hito]').forEach((h, i) => {
        h.classList.toggle('is-hecho', m[i]);
        h.classList.toggle('is-sig', !m[i] && sig === i);
      });
      el.querySelectorAll('.bubu-esc__etq').forEach((h, i) => {
        h.classList.toggle('is-hecho', m[i]);
        h.classList.toggle('is-sig', !m[i] && sig === i);
      });
    });
  },

  /**
   * ADOPCIÓN: un nodo no se borra por haber cambiado de key.
   *
   * Aquí estaba el «Monta tu caja dice 3 y está vacía». Una línea provisional se pinta
   * con una key inventada (bubu-op-7); cuando Shopify la confirma, esa misma línea pasa
   * a tener la key de verdad. El nodo de la pantalla se quedaba con la key vieja, y
   * como esa key ya no existía en el modelo, se borraba. Volvía solo si llegaba a
   * tiempo un repintado del servidor con el HTML bueno; cuando ese repintado se saltaba
   * —y se salta a propósito si el cliente sigue tocando— el Bubu se iba para siempre
   * aunque el carrito siguiera teniéndolo. De ahí el contador diciendo una cosa y la
   * caja enseñando otra.
   *
   * Ahora, antes de borrar nada, se mira si hay una línea del modelo SIN nodo y de la
   * MISMA variante: si la hay, es la misma de antes con otra key, y el nodo se la queda.
   * Sin parpadeo, sin volver a descargar la foto. Solo se va el nodo que de verdad no
   * tiene ninguna línea detrás.
   */
  _sinNodo(sel) {
    const puestas = new Set();
    document.querySelectorAll(sel).forEach((n) => { if (n.dataset.key) puestas.add(n.dataset.key); });
    return this.cart.items.filter((i) => !puestas.has(i.key));
  },
  _adopta(nodo, libres) {
    const v = Number(nodo.getAttribute('data-variant-id') || 0);
    if (!v) return null;
    const i = libres.findIndex((l) => Number(l.variant_id) === v);
    if (i < 0) return null;
    const l = libres.splice(i, 1)[0];
    nodo.dataset.key = l.key;
    nodo.querySelectorAll('[data-key]').forEach((b) => { b.dataset.key = l.key; });
    return l;
  },

  /** Líneas de la cesta, huecos de la caja y totales, sin esperar al servidor. */
  _lineasLocal() {
    if (!this.cart) return;
    const libresFilas = this._sinNodo('[data-bubu-linea][data-key]');
    document.querySelectorAll('[data-bubu-linea]').forEach((fila) => {
      let l = this.linea(fila.dataset.key);
      if (!l) l = this._adopta(fila, libresFilas);
      if (!l) { sale(fila, true); return; }
      fila.dataset.qty = l.quantity;
      const n = fila.querySelector('.bubu-stepper__n');
      if (n) n.textContent = l.quantity;
      const p = fila.querySelector('.bubu-cesta__precio');
      if (p && !p.querySelector('.bubu-precio')) p.textContent = money(l.final_line_price);
    });
    const libresSlots = this._sinNodo('[data-bubu-slot][data-key]');
    document.querySelectorAll('[data-bubu-slot][data-key]').forEach((slot) => {
      let l = this.linea(slot.dataset.key);
      if (!l) l = this._adopta(slot, libresSlots);
      if (!l) { sale(slot, false); return; }
      const n = slot.querySelector('.bubu-slot__n');
      if (l.quantity > 1) {
        if (n) n.textContent = '×' + l.quantity;
        else { const s = document.createElement('span'); s.className = 'bubu-slot__n'; s.textContent = '×' + l.quantity; slot.appendChild(s); }
      } else if (n) n.remove();
    });
    // Toda línea que el cliente ve como suya y NO tiene nodo se pinta aquí: las recién
    // añadidas (para que el Bubu aparezca en el acto, antes de que conteste Shopify) y
    // también cualquiera que se haya quedado sin nodo por el camino. Esto último es la
    // segunda mitad de la red de seguridad: pase lo que pase con los repintados, lo que
    // dice el contador y lo que se ve en la caja SIEMPRE son lo mismo. Los regalos (el
    // activador a 0 €) y los packs de fiesta tienen su propio sitio y no ocupan hueco.
    this.cart.items.filter((i) => !esRegalo(i) && !esPack(i)).forEach((l) => {
      document.querySelectorAll('[data-bubu-slots]').forEach((cont) => {
        if (cont.querySelector(`[data-key="${l.key}"]`)) return;
        // El hueco de la casilla MÁS BAJA, no el primero del DOM: desde que el escenario
        // manda (data-celda), el orden del DOM y el de la pantalla ya no son el mismo, y
        // el Bubu tiene que aparecer en el primer hueco que se ve, no en otro.
        const hueco = Array.from(cont.querySelectorAll('.bubu-slot.is-vacio'))
          .sort((x, y) => (Number(x.dataset.celda) || 0) - (Number(y.dataset.celda) || 0))[0];
        const slot = document.createElement('div');
        slot.className = 'bubu-slot is-lleno';
        slot.dataset.bubuSlot = '';
        slot.dataset.grupo = grupoDom(l, this.cart.items);
        slot.dataset.key = l.key;
        slot.setAttribute('data-variant-id', l.variant_id);
        const fotoCaja = l.imageCaja || anchoImg(l.image, 200);
        slot.innerHTML = (fotoCaja ? `<img src="${fotoCaja}" alt="" decoding="async">` : '') + '<button type="button" class="bubu-slot__quitar" data-bubu-quitar data-key="' + l.key + '" aria-label="Quitar">×</button>';
        if (hueco) hueco.replaceWith(slot); else cont.appendChild(slot);
        // El pop va AQUÍ, en el instante en que nace la casilla. Antes lo ponía
        // bubu-tienda.js al llegar el repintado del servidor: el Bubu aparecía al tocar
        // y medio segundo después pegaba el salto, así que una sola acción se veía como
        // dos. La cabecera de este archivo ya lo dice: las animaciones de añadir viven
        // aquí y solo aquí. «is-nuevo» está en TRANSITORIAS, así que si el repintado cae
        // en mitad del pop no lo corta.
        slot.classList.add('is-nuevo');
        setTimeout(() => slot.classList.remove('is-nuevo'), 380);
        document.dispatchEvent(new CustomEvent('bubu:slot-nuevo', { detail: { slot: slot, cont: cont } }));
      });
      document.querySelectorAll('[data-bubu-lineas]').forEach((cont) => {
        if (cont.querySelector(`[data-key="${l.key}"]`)) return;
        const fotoCesta = l.imageCesta || anchoImg(l.image, 120);
        const fila = document.createElement('div');
        fila.className = 'bubu-cesta__linea' + (l._provisional ? ' is-provisional' : '');
        fila.setAttribute('data-bubu-linea', '');
        fila.dataset.grupo = grupoDom(l, this.cart.items);
        fila.dataset.key = l.key;
        fila.setAttribute('data-variant-id', l.variant_id);
        fila.dataset.qty = l.quantity;
        fila.innerHTML =
          '<span class="bubu-cesta__foto">' + (fotoCesta ? `<img src="${fotoCesta}" alt="" decoding="async">` : '') + '</span>' +
          '<div class="bubu-cesta__info"><div class="bubu-cesta__nombre">' + (l.product_title || l.title || '') + '</div>' +
          // El stepper provisional se pinta con los MISMOS <button> que manda el
          // servidor. Con <span> se veía torcido (un botón trae sus propias métricas de
          // caja y de línea) y, peor, no se podía pulsar hasta que contestaba el carrito.
          '<div class="bubu-stepper bubu-stepper--cesta" data-bubu-stepper-cesta>' +
          '<button type="button" class="bubu-stepper__btn" data-bubu-cesta-menos aria-label="Quitar uno">−</button>' +
          '<span class="bubu-stepper__n">' + l.quantity + '</span>' +
          '<button type="button" class="bubu-stepper__btn bubu-stepper__btn--mas" data-bubu="add-mas" data-bubu-cesta-mas data-bubu-mas aria-label="Añadir uno">+</button>' +
          '</div></div>' +
          '<span class="bubu-cesta__precio">' + money(l.final_line_price) + '</span>';
        cont.appendChild(fila);
      });
      document.querySelectorAll('.bubu-cesta__vacia').forEach((v) => (v.hidden = true));
    });
    // La píldora «N uds» cuenta lo MISMO que la escalera (antes sumaba también los
    // accesorios y se contradecía con la barra que tiene justo encima).
    const uds = this._uds();
    const texto = uds === 1 ? '1 ud' : uds > 1 ? uds + ' uds' : '—';
    document.querySelectorAll('[data-bubu-caja-pill]').forEach((el) => { el.textContent = texto; });
    // El contador de la barra móvil y el importe del botón los pintaba SOLO el servidor,
    // así que durante medio segundo decían una cosa mientras la barra de hitos que
    // tienen pegada al lado decía otra. Ahora los tres salen del mismo sitio.
    document.querySelectorAll('[data-bubu-uds]').forEach((el) => { el.textContent = texto; });
    document.querySelectorAll('.bubu-caja__total').forEach((el) => { el.textContent = '· ' + money(this.cart.total_price); });
    document.querySelectorAll('[data-bubu-caja]').forEach((c) => { c.dataset.uds = uds; c.classList.toggle('is-vacia', uds === 0); });
    const tot = document.querySelector('.bubu-cesta__total-n');
    if (tot) tot.textContent = money(this.cart.total_price);
    // «Elige tu primer Bubu» ↔ «Ver tu caja». Los dos botones vienen pintados del
    // servidor y aquí solo se enseña el que toca, para que el cambio ocurra en el mismo
    // instante del clic y no medio segundo después.
    const vacia = this.cart.items.filter((i) => !esRegalo(i)).length === 0;
    document.querySelectorAll('[data-bubu-cta-vacio]').forEach((el) => el.classList.toggle('is-oculto', !vacia));
    document.querySelectorAll('[data-bubu-cta-lleno]').forEach((el) => el.classList.toggle('is-oculto', vacia));
  },

  /**
   * Unidades que cuentan para la escalera y para el mensaje del upsell.
   * Antes se deducía del precio (todo lo que valiera 13,95 o más), y eso dejaba fuera
   * al Mystery Slime —que es un slime— mientras la Tarta sí contaba. Ahora la lista de
   * lo que NO suma llega de Liquid (snippets/bubu-config.liquid, etiqueta «no-cuenta»),
   * así que esta cuenta y la del servidor son la misma y la barra nunca da un salto al
   * llegar la respuesta.
   */
  _uds() {
    return this.cart ? udsCuentan(this.cart.items) : 0;
  },

  /** «Combina tus favoritos» cambia de mensaje según lo que llevas (prototipo). */
  _upsellTexto() {
    const t = document.querySelector('[data-bubu-upsell-t]');
    if (!t) return;
    const u = this._uds();
    /* EN EUROS (05/10/2026): con 2, 3 o 4 Bubus el titular dice lo que cuesta el
       siguiente, con el precio del Bubu de esta ficha (data-precio). Misma cuenta que
       snippets/bubu-siguiente.liquid, que pinta lo mismo desde el servidor. */
    const caja = document.querySelector('[data-bubu-upsell]');
    const base = caja ? Number(caja.dataset.precio) || 0 : 0;
    const E = CFG.escalera || [{ uds: 3, pct: 10 }, { uds: 4, pct: 0 }, { uds: 5, pct: 15 }];
    const pctCon = (n) => E.reduce((a, t) => (n >= t.uds && t.pct > a ? t.pct : a), 0);
    const items = this.cart ? this.cart.items : [];
    const totalAhora = this.cart ? this.cart.total_price : 0;
    const hayPack = items.some(esPack);
    const umbral = Number(CFG.envioUmbral || 0);
    const cuesta = u >= 2 && u <= 4 && !hayPack ? cuestaSiguiente(items, pctCon(u + 1), base) : 0;
    /* EL ESTADO DE 1 UNIDAD ERA EL AGUJERO. Decía «Prueba algo diferente · añade otra
       textura, aroma o personaje a tu caja», que es bonito pero no da ningún motivo para
       coger el segundo Bubu, y esta tira está pegada al botón de añadir: es el sitio con
       más poder de toda la ficha. Ahora nombra el premio que está a una unidad. */
    const yaEnvio = umbral > 0 && totalAhora >= umbral;
    const conEnvio = umbral > 0 && totalAhora + cuesta >= umbral;
    /* Textos cortos (05/10/2026), los mismos que snippets/bubu-upsell.liquid: el titular
       cabe en 184 px (móvil de 360 con las flechas) y la segunda línea en 270 px. */
    const T = u >= 5 ? ['Ya tienes el −15 %.', 'Todo lo que añadas, al ', '15 % menos.']
      : u === 4 && cuesta > 0 ? [`El quinto, por ${money(cuesta)}.`, 'Y toda la caja con un ', '15 % menos.']
        : u === 4 ? ['Con 5, todo al −15 %.', 'Añade uno más y ', 'lo tienes.']
          : u === 3 && cuesta > 0 && yaEnvio ? [`El cuarto, por ${money(cuesta)}.`, 'Y te quedas a uno del ', '15 %.']
            : u === 3 && cuesta > 0 && conEnvio ? [`El cuarto, por ${money(cuesta)}.`, 'Y con él, envío gratis a ', 'Península.']
            : u === 3 ? ['Más cerca del envío.', 'Envío gratis a Península desde ', `${money(umbral || 5000).replace(',00', '')}.`]
              : u === 2 && cuesta > 0 ? [`El tercero, por ${money(cuesta)}.`, 'Y los tres con un ', '10 % menos.']
                : u === 2 ? ['Con 3, todo al −10 %.', 'Añade uno más y ', 'lo tienes.']
                  : u === 1 ? ['El segundo trae regalo.', 'Con 2 Bubus te regalamos el ', 'activador.']
                    : ['Combina tus favoritos.', 'Cada uno con su textura y ', 'aroma.'];
    t.textContent = T[0];
    const a = document.querySelector('[data-bubu-upsell-a]');
    const b = document.querySelector('[data-bubu-upsell-b]');
    if (a) a.textContent = T[1];
    if (b) b.textContent = T[2];
  },

  /**
   * La tira de «Añade tus extras» sigue a los hitos: con un pack de evento en el carrito
   * y nada que sume para los tramos, las dos se van.
   *
   * El motivo es el mismo que el de la escalera, y se ve mejor con el caso delante: se
   * encarga un Pack Fiesta para treinta invitados —180 €— y la pantalla contesta con
   * «Añade 3 Bubus y ahorra un 10 %» sobre una barra vacía y una fila ofreciendo un
   * activador a 0,95 € que ese pack ya lleva dentro del precio («2 activadores para el
   * adulto»). Las dos cosas son ruido, y la segunda además vende algo ya comprado.
   *
   * Vuelven en cuanto entra algo que SUMA (slime, Mystery, Tarta). Un accesorio
   * «no-cuenta» o un lote cerrado no las devuelven: no mueven ni un hito, así que la
   * barra reaparecería con el mismo mensaje sobre la misma barra vacía.
   *
   * Va aquí y no dentro de _escaleraLocal porque esa función se planta si en la página
   * no hay ninguna escalera: en esa página los extras se quedarían sin regla.
   */
  _extrasLocal() {
    const bloques = document.querySelectorAll('[data-bubu-extras-bloque]');
    if (!bloques.length || !this.cart) return;
    const soloPack = this.cart.items.some(esPack) && this._uds() === 0;
    bloques.forEach((el) => el.classList.toggle('is-oculta', soloPack));
  },

  /* EL PIE DEL CARRITO TAMBIÉN SALE DEL MODELO. El Total ya se pintaba en el acto
     (.bubu-cesta__total-n, en _lineasLocal) pero el Subtotal, el Descuento, el Envío y
     «Ahorras» los pintaba SOLO el servidor: al pulsar «+» el Total saltaba a 34,98 €
     mientras dos renglones más arriba el Subtotal seguía diciendo 32,85 € y el
     Descuento el de antes, hasta que contestaba Shopify medio segundo después. Las
     filas vienen SIEMPRE en el HTML —ocultas con `hidden` cuando valen cero, ver
     snippets/bubu-cesta-contenido.liquid—, así que aquí solo hay que rellenar el número
     y enseñar u ocultar. Los importes son los mismos que ya usa el Total, o sea que no
     puede quedar un pie que no cuadre consigo mismo. */
  _totalesLocal() {
    const c = this.cart;
    if (!c) return;
    // El número vive dentro de un <span class="bubu-precio"> que pinta Liquid; si está,
    // se escribe ahí y el resto del renglón («Ahorras», el «−») se queda como estaba.
    const pinta = (el, txt) => { if (!el) return; const p = el.querySelector('.bubu-precio'); (p || el).textContent = txt; };
    const sub = c.original_total_price || 0;
    const desc = Math.max(0, sub - (c.total_price || 0));
    const pct = sub > 0 ? Math.round((desc * 100) / sub) : 0;
    document.querySelectorAll('[data-bubu-subtotal]').forEach((el) => pinta(el, money(sub)));
    document.querySelectorAll('[data-bubu-desc-fila]').forEach((el) => { el.hidden = desc <= 0; });
    document.querySelectorAll('[data-bubu-desc-n]').forEach((el) => pinta(el, money(desc)));
    document.querySelectorAll('[data-bubu-desc-pct]').forEach((el) => { el.hidden = pct <= 0; el.textContent = '−' + pct + ' %'; });
    // Misma regla de envío gratis que la escalera y que snippets/bubu-cesta-contenido:
    // manda el importe contra el umbral; las unidades solo si el umbral está a cero.
    const umbral = Number(CFG.envioUmbral || 0);
    const E = CFG.escalera || [{ uds: 3, pct: 10 }, { uds: 4, pct: 0 }, { uds: 5, pct: 15 }];
    const pack = c.items.some(esPack);
    const gratis = umbral > 0 ? (c.total_price || 0) >= umbral || pack : this._uds() >= E[1].uds || pack;
    document.querySelectorAll('[data-bubu-envio-gratis]').forEach((el) => { el.hidden = !gratis; });
    document.querySelectorAll('[data-bubu-envio-nota]').forEach((el) => { el.hidden = gratis; });
    // LOS REGALOS CUENTAN COMO AHORRO (21/09/2026). Lo que vale cada regalo viene de
    // CFG.regalosValor (el compare_at de su variante a 0 €, por id de variante), la
    // misma cifra que el servidor pinta tachada en su línea. Así el «Ahorras» salta a
    // 4,95 € en el mismo instante en que el activador entra en la caja, no medio
    // segundo después, y dice lo mismo que dirá Shopify al repintar.
    const valores = CFG.regalosValor || {};
    const regalos = c.items.reduce((a, i) => (esRegalo(i) ? a + (Number(valores[String(i.variant_id)]) || 0) * (i.quantity || 1) : a), 0);
    const ahorro = desc + regalos;
    document.querySelectorAll('[data-bubu-regalos-fila]').forEach((el) => { el.hidden = regalos <= 0; });
    document.querySelectorAll('[data-bubu-regalos-n]').forEach((el) => { el.textContent = money(regalos); });
    document.querySelectorAll('[data-bubu-ahorras]').forEach((el) => { el.hidden = ahorro <= 0; pinta(el, money(ahorro)); });
    // «Estás ahorrando …» del pie de «Monta tu caja»: un solo renglón, siempre en el
    // HTML, que se rellena y se enseña u oculta aquí con la misma cuenta.
    document.querySelectorAll('.bubu-caja__ahorro').forEach((fila) => {
      pinta(fila.querySelector('strong') || fila, money(ahorro));
      fila.hidden = ahorro <= 0;
    });
  },

  _pintaRapido() {
    this._contadores();
    this._escaleraLocal();
    this._extrasLocal();
    this._lineasLocal();
    this._totalesLocal();
    this._ajustaHuecos();
    this._upsellTexto();
  },

  // ---------- Repintado ----------
  _repinta(sections, detalle) {
    const hechosAntes = this._hitosHechos();
    if (sections) {
      this._debePintar = false;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const cont = tmp.querySelector(`[data-${id}-cont]`);
        const destinos = cont ? document.querySelectorAll(`[data-${id}-cont]`) : document.querySelectorAll(`#shopify-section-${id}`);
        destinos.forEach((el) => {
          const nuevo = cont || tmp.querySelector(`#shopify-section-${id}`);
          if (nuevo) {
            try {
              morphHijos(el, nuevo);
            } catch (_) {
              el.innerHTML = nuevo.innerHTML;
            }
          } else {
            el.innerHTML = html;
          }
          document.dispatchEvent(new CustomEvent('bubu:seccion-repintada', { detail: { id, raiz: el, ...detalle } }));
        });
      });
    }
    this._espejos();
    // SIEMPRE se repinta desde el modelo después de morphear. El HTML que acaba de
    // llegar es la estructura; los números los pone el modelo. Antes esto era
    // condicional y se quedaban mezcladas las dos verdades en la misma barra: «1 ud» y
    // «13,95 €» del servidor al lado de una barra de hitos pintada por el JS que creía
    // llevar cinco. Como el modelo es derivado, pintarlo de más no puede descuadrar
    // nada.
    this._pintaRapido();
    this._animaHitos(hechosAntes);
    document.dispatchEvent(new CustomEvent('bubu:cart', { detail: { cart: this.cart, ...detalle } }));
  },

  /** Escaleras «espejo» (ficha): copian la escalera recién repintada de la caja/cesta. */
  _espejos() {
    const fuente = document.querySelector('[data-bubu-caja] [data-bubu-escalera], [data-bubu-cesta-cont] [data-bubu-escalera]');
    if (!fuente) return;
    // Con `innerHTML` la escalera espejo de la ficha se DEMOLÍA en cada repintado: el
    // relleno nacía de cero en su nueva anchura, así que la transición de CSS no tenía
    // desde dónde salir y la barra pegaba un salto en vez de deslizarse. Emparejando el
    // árbol, el <span> del relleno es el mismo de antes y la anchura sí transiciona.
    const tmp = document.createElement('div');
    tmp.innerHTML = fuente.outerHTML;
    document.querySelectorAll('[data-bubu-esc-espejo-in]').forEach((el) => {
      try {
        morphHijos(el, tmp);
      } catch (_) {
        el.innerHTML = fuente.outerHTML;
      }
    });
  },

  _hitosHechos() {
    const esc = document.querySelector('[data-bubu-escalera]');
    return esc ? esc.querySelectorAll('.bubu-esc__hito.is-hecho').length : 0;
  },
  _animaHitos(antes) {
    const ahora = this._hitosHechos();
    if (ahora <= antes) return;
    document.querySelectorAll('[data-bubu-escalera]').forEach((esc) => {
      esc.querySelectorAll('.bubu-esc__hito.is-hecho').forEach((h, i) => {
        if (i >= antes) h.classList.add('is-pop');
      });
    });
  },

  _contadores() {
    if (!this.cart) return;
    const n = this.cart.items.filter((i) => !esRegalo(i)).reduce((a, i) => a + i.quantity, 0);
    document.querySelectorAll('[data-bubu-contador]').forEach((el) => (el.textContent = n));
    document.querySelectorAll('[data-bubu-contador-badge]').forEach((el) => {
      el.textContent = n;
      el.classList.toggle('is-vacio', n === 0);
    });
    document.querySelectorAll('.bubu-cab__carrito, .bubu-cab__icono[data-bubu-abrir-cesta]').forEach((b) => {
      b.classList.remove('is-bote');
      void b.offsetWidth;
      b.classList.add('is-bote');
    });
    // Cantidades por producto y por variante, contadas UNA vez. Antes cada tarjeta
    // recorría el carrito entero por su cuenta: con 60 tarjetas en la tienda y diez
    // líneas en el carrito eran 600 recorridos en cada repintado, y el repintado pasa
    // en cada clic. Ahora son dos mapas y una consulta por tarjeta.
    const porProducto = new Map();
    const porVariante = new Map();
    this.cart.items.forEach((i) => {
      if (esRegalo(i) || esPack(i)) return;
      const p = Number(i.product_id);
      const v = Number(i.variant_id);
      porProducto.set(p, (porProducto.get(p) || 0) + i.quantity);
      porVariante.set(v, (porVariante.get(v) || 0) + i.quantity);
    });
    // Tarjetas: cantidad por producto.
    document.querySelectorAll('[data-bubu-tarjeta]').forEach((t) => {
      const q = porProducto.get(Number(t.dataset.productId)) || 0;
      t.dataset.qty = q;
      t.classList.toggle('is-en-caja', q > 0);
      // La tarjeta de la tienda ya no lleva selector de cantidad: la cifra solo se
      // enseña a partir de la segunda unidad, que es cuando aporta algo.
      t.classList.toggle('is-multi', q > 1);
      const qtyEl = t.querySelector('[data-bubu-qty]');
      if (qtyEl) qtyEl.textContent = q;
      const btn = t.querySelector('[data-bubu-anadir]');
      const st = t.querySelector('[data-bubu-stepper]');
      // El botón de añadir solo se esconde donde hay un stepper que ocupe su sitio (la
      // tarjeta de la tienda). Las miniaturas del upsell también son [data-bubu-tarjeta]
      // pero no tienen stepper: escondiéndoles el «+» se quedaban sin forma de añadir.
      if (btn && st) btn.classList.toggle('is-oculto', q > 0);
      if (st) st.classList.toggle('is-oculto', q === 0);
    });
    // Extras (caja y cesta): misma insignia «×N» que las miniaturas del upsell. El «+»
    // NUNCA se sustituye por el número: si desaparece, nadie entiende que puede añadir
    // un segundo activador. El número va en su propia píldora, arriba a la izquierda.
    document.querySelectorAll('[data-bubu-extra]').forEach((t) => {
      const q = porVariante.get(Number(t.dataset.variantId)) || 0;
      t.dataset.qty = q;
      t.classList.toggle('is-en-caja', q > 0);
      const qtyEl = t.querySelector('[data-bubu-qty]');
      if (qtyEl) qtyEl.textContent = q;
    });
  },

  _error(data) {
    const msg = (data && (data.description || data.message)) || 'No se ha podido actualizar el carrito.';
    document.dispatchEvent(new CustomEvent('bubu:cart-error', { detail: { mensaje: msg } }));
    console.warn('[bubu-cart]', msg);
  },
};

window.BubuCart = BubuCart;
window.BubuAnim = BubuAnim;
window.bubuMoney = money;

// ---------- Aviso de error ----------
document.addEventListener('bubu:cart-error', (e) => {
  document.querySelectorAll('.bubu-toast').forEach((t) => t.remove());
  const t = document.createElement('div');
  t.className = 'bubu-toast';
  t.setAttribute('role', 'alert');
  t.textContent = e.detail.mensaje;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 3200);
});

// ---------- Delegación de clics: tarjetas, packs, extras, steppers, cesta ----------
// Cola en serie. Antes se descartaba el clic si había otra petición en marcha: al
// pulsar dos veces seguidas un extra, el segundo toque se perdía y la animación se
// quedaba a medias. Ahora ninguno se pierde: se pintan al instante y las peticiones
// salen una detrás de otra.
let cola = Promise.resolve();
function conBloqueo(fn) {
  cola = cola.then(fn).catch(() => {});
  return cola;
}

document.addEventListener('click', (e) => {
  // Abrir cesta / hoja «Monta tu caja».
  const abrirCesta = e.target.closest('[data-bubu-abrir-cesta]');
  if (abrirCesta) {
    e.preventDefault();
    BubuCart.abrirCesta(abrirCesta);
    return;
  }
  const abrirCaja = e.target.closest('[data-bubu-abrir-caja]');
  if (abrirCaja) {
    e.preventDefault();
    BubuCart.abrirCaja(abrirCaja);
    return;
  }
  const seguir = e.target.closest('[data-bubu-cerrar-y-seguir]');
  if (seguir) {
    const cesta = document.getElementById('bubu-cesta');
    if (cesta && cesta.cerrar) cesta.cerrar();
    return; // el enlace sigue su curso
  }

  // Añadir (botón grande de ficha/packs cerrados: data-bubu-add; pequeño: data-bubu-anadir).
  // El CTA de la ficha de un pack lleva data-bubu-add (hereda el estilo de botón
  // grande) Y data-bubu-anadir-pack: no tiene variante propia, así que esta rama lo
  // deja pasar a la del pack. Antes hacía return aquí por falta de data-variant-id y
  // el botón de la ficha de pack no añadía nada.
  const grande = e.target.closest('[data-bubu-add]');
  if (grande && !grande.hasAttribute('data-bubu-anadir-pack')) {
    e.preventDefault();
    const id = grande.dataset.variantId;
    if (!id) return;
    const qty = Number(grande.dataset.quantity || 1);
    BubuAnim.grande(grande);
    // Se pinta en el acto (fuera de la cola) y solo la petición va encolada.
    BubuCart.pideAnadir([{ id, quantity: qty }], grande);
    return;
  }
  const anadir = e.target.closest('[data-bubu-anadir]');
  if (anadir) {
    e.preventDefault();
    const id = anadir.dataset.variantId;
    if (!id) return;
    BubuAnim.mas(anadir);
    BubuCart.pideAnadir([{ id, quantity: 1 }], anadir);
    return;
  }
  const pack = e.target.closest('[data-bubu-anadir-pack]');
  if (pack) {
    e.preventDefault();
    const cont = pack.closest('[data-bubu-pack]');
    const ids = ((cont && cont.dataset.items) || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!ids.length) return;
    // Botón grande de ficha (data-bubu-add): barrido como cualquier CTA de ficha;
    // botón pequeño de tarjeta: el pulso corto.
    if (pack.hasAttribute('data-bubu-add')) BubuAnim.grande(pack);
    else BubuAnim.mas(pack);
    // Los packs de componentes entran como líneas normales, una por Bubu. Un lote
    // cerrado (Caja Secreta) entra como UNA línea marcada con _lote: no cuenta para la
    // escalera ni ocupa un hueco de la caja, porque su unidad va por debajo del base.
    const props = cont && cont.dataset.lote ? { _lote: 'cerrado' } : undefined;
    // Foto y nombre de cada componente (data-items-fotos / data-items-nombres, en el
    // mismo orden que data-items): la línea provisional se pinta con su slime desde el
    // clic. Si un componente no trae foto, esa línea va sin pista y la pone Shopify.
    const fotos = ((cont && cont.dataset.itemsFotos) || '').split(',');
    const nombres = ((cont && cont.dataset.itemsNombres) || '').split('|');
    const precios = ((cont && cont.dataset.itemsPrecios) || '').split(',');
    const pistaDe = (i) => {
      const src = (fotos[i] || '').trim();
      const precio = Number((precios[i] || '').trim()) || 0;
      if (!src && !precio) return undefined;
      const caja = anchoImg(src, 200);
      const cesta = anchoImg(src, 120);
      precarga(caja);
      precarga(cesta);
      return { img: src, imgCaja: caja, imgCesta: cesta, titulo: (nombres[i] || '').trim(), precio: precio };
    };
    BubuCart.pideAnadir(ids.map((id, i) => ({ id, quantity: 1, properties: props, pistas: pistaDe(i) })), pack);
    // Rastro del pack para los informes (ver marcaPack). Sale 1,5 s después y por la
    // cola, detrás del añadido: no retrasa ni la animación ni la petición del cliente.
    const nombrePack = ((cont && (cont.dataset.packNombre || cont.dataset.nombre)) || '').trim();
    if (nombrePack) setTimeout(() => conBloqueo(() => BubuCart.marcaPack(nombrePack)), 1500);
    return;
  }
  const mas = e.target.closest('[data-bubu-mas-uno]');
  if (mas) {
    e.preventDefault();
    const t = mas.closest('[data-bubu-tarjeta]');
    if (!t) return;
    BubuAnim.mas(mas);
    BubuCart.pideAnadir([{ id: t.dataset.variantId, quantity: 1 }], mas);
    return;
  }
  const menos = e.target.closest('[data-bubu-menos]');
  if (menos) {
    e.preventDefault();
    const t = menos.closest('[data-bubu-tarjeta]');
    if (!t) return;
    const linea = BubuCart.lineaVariante(t.dataset.variantId);
    if (!linea) return;
    if (linea.quantity > 1) BubuQuita(t.querySelector('[data-bubu-stepper]') || t);
    BubuCart.pideCambio(linea.key, Math.max(0, linea.quantity - 1));
    return;
  }
  // Steppers de la cesta.
  const cMas = e.target.closest('[data-bubu-cesta-mas]');
  const cMenos = e.target.closest('[data-bubu-cesta-menos]');
  if (cMas || cMenos) {
    e.preventDefault();
    const fila = (cMas || cMenos).closest('[data-bubu-linea]');
    const linea = fila && BubuCart.linea(fila.dataset.key);
    if (!linea) return;
    // Una línea provisional ya se puede tocar: como lo que se manda es la diferencia
    // entre lo que quieres y lo que hay, subirla o bajarla antes de que conteste el
    // carrito simplemente cambia lo que va a salir (o hace que no salga).
    if (cMas) BubuAnim.mas(cMas);
    else if (linea.quantity > 1) BubuQuita(fila);
    BubuCart.pideCambio(linea.key, Math.max(0, linea.quantity + (cMas ? 1 : -1)));
    return;
  }
  const quitarPack = e.target.closest('[data-bubu-quitar-pack]');
  if (quitarPack) {
    e.preventDefault();
    const pid = quitarPack.getAttribute('data-bubu-quitar-pack');
    if (!pid || !BubuCart.cart) return;
    // Un pack de cumple son seis pasos de configuración. Borrarlo de un solo toque,
    // con una «×» pequeña y el dedo cerca, es demasiado fácil: el primer toque pide
    // confirmación en el propio botón y el segundo borra. A los 4 s se olvida.
    if (quitarPack.dataset.confirmar !== '1') {
      quitarPack.dataset.confirmar = '1';
      quitarPack.dataset.textoPrevio = quitarPack.textContent;
      quitarPack.textContent = '¿Seguro?';
      quitarPack.classList.add('is-confirmando');
      quitarPack.setAttribute('aria-label', 'Confirmar que quieres quitar el pack');
      clearTimeout(quitarPack._bubuConf);
      quitarPack._bubuConf = setTimeout(() => {
        quitarPack.dataset.confirmar = '';
        quitarPack.textContent = quitarPack.dataset.textoPrevio || '×';
        quitarPack.classList.remove('is-confirmando');
        quitarPack.setAttribute('aria-label', 'Quitar el pack');
      }, 4000);
      return;
    }
    clearTimeout(quitarPack._bubuConf);
    const updates = {};
    BubuCart.cart.items.forEach((i) => {
      if (i.properties && i.properties._pack_id === pid) updates[i.key] = 0;
    });
    if (!Object.keys(updates).length) return;
    // Sin conBloqueo aquí: update() ya entra en la cola por su cuenta y envolverlo otra
    // vez dejaría la cola esperándose a sí misma.
    BubuCart.update(updates);
    return;
  }
  const quitar = e.target.closest('[data-bubu-quitar]');
  if (quitar) {
    e.preventDefault();
    const key = quitar.dataset.key;
    if (!key) return;
    // La «×» del hueco quita UNA unidad, no la línea entera. Con un «×3» en la caja,
    // llevarse los tres de un solo toque no es lo que espera nadie: se va restando y,
    // al llegar a cero, el hueco desaparece solo.
    const lQ = BubuCart.linea(key);
    const qQ = lQ && lQ.quantity > 1 ? lQ.quantity - 1 : 0;
    if (qQ > 0) BubuQuita(quitar.closest('[data-bubu-slot]') || quitar);
    BubuCart.pideCambio(key, qQ);
  }
});

/* ------------------------------------------------------------------
   El pack de cumple VUELA al carrito. Es el remate del asistente en el diseño: una
   píldora blanca con el icono de regalo aparece abajo en el centro y sale hacia la
   esquina del carrito (bubuFlyCart .8s). El tema tenía la animación escrita pero nunca
   la disparaba. Vive aquí, escuchando el evento que ya emite bubu-cumples.js, para no
   tener que tocar el asistente.
   ------------------------------------------------------------------ */
document.addEventListener('bubu:pack-cumple', () => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const v = document.createElement('div');
  v.className = 'bubu-vuela';
  v.setAttribute('aria-hidden', 'true');
  v.innerHTML =
    '<span class="bubu-vuela__ico">' +
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
    '<path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg></span>' +
    '<span class="bubu-vuela__t">Pack de cumple</span>';
  document.body.appendChild(v);
  const fin = () => v.remove();
  v.addEventListener('animationend', fin, { once: true });
  setTimeout(fin, 1100);
});

/* ------------------------------------------------------------------
   NADIE LLEGA A PAGAR CON CAMBIOS SIN MANDAR.

   El botón de pagar era un enlace pelado a /checkout. Entre que tocas algo y que sale
   la petición hay 120 ms de espera (para que cinco toques seguidos viajen juntos), más
   lo que tarde Shopify en contestar. En esa ventana la PANTALLA ya te ha hecho caso
   pero el servidor no se ha enterado todavía. Quitar un slime y pulsar «Finalizar
   compra» acto seguido es un gesto de lo más normal —te arrepientes y pagas— y así se
   pagaba el carrito de antes del arrepentimiento. Eso no es un parpadeo: es un pedido
   equivocado.

   Ahora el enlace espera. Se manda lo que falte, se espera a que Shopify conteste y
   entonces se navega. Casi siempre son menos de 400 ms y no se nota. Si el carrito no
   contestara, a los 4 s se va igual: dejar a alguien encerrado delante del botón de
   pagar sería peor que el problema que se está evitando.
   ------------------------------------------------------------------ */
document.addEventListener(
  'click',
  (e) => {
    const pagar = e.target.closest && e.target.closest('[data-bubu-checkout]');
    if (!pagar) return;
    // Carrito vacío: el enlace está ahí por el diseño, pero no lleva a ninguna parte.
    if (pagar.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      return;
    }
    if (pagar.dataset.bubuYendo === '1') return;
    e.preventDefault();
    pagar.dataset.bubuYendo = '1';
    // El acuse de recibo va en estilo directo y no en una clase de la hoja: es un estado
    // que dura lo que tarda una petición, lo pone y lo quita este archivo, y no merece
    // una regla en bubu-cesta.css que haya que ir a buscar para entender el botón. No
    // cambia el color ni el texto —el botón sigue diciendo lo mismo—, solo deja de
    // aceptar toques para que nadie lo pulse dos veces.
    pagar.style.pointerEvents = 'none';
    pagar.style.opacity = '0.82';
    const destino = pagar.getAttribute('href') || '/checkout';
    let ido = false;
    const vete = () => {
      if (ido) return;
      ido = true;
      window.location.href = destino;
    };
    setTimeout(vete, 4000);
    Promise.resolve(BubuCart.vacia()).then(vete, vete);
  },
  true
);

/* ------------------------------------------------------------------
   Cambiar de pestaña o bloquear el móvil tampoco puede dejar nada a medias.

   Los 120 ms de espera son un setTimeout, y el navegador CONGELA los temporizadores de
   una pestaña que no se ve (Chrome los deja en uno por minuto). Si tocas «−» y te vas
   a otra app, ese aviso se queda dormido: la pantalla ya dice una cosa y Shopify otra,
   y así se queda hasta que vuelvas. Medido: 72 segundos con seis borrados sin mandar.

   Al irse, se manda lo que haya pendiente sin esperar al temporizador. Al volver, se
   comprueba otra vez por si el navegador cortó algo por el camino.
   ------------------------------------------------------------------ */
function alSalirDeLaPagina() {
  if (!BubuCart._base || !BubuCart.hayPendientes()) return;
  clearTimeout(BubuCart._t);
  conBloqueo(() => BubuCart._concilia());
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') alSalirDeLaPagina();
  else BubuCart._revisa();
});
window.addEventListener('pagehide', alSalirDeLaPagina);

/* Red de seguridad: si por lo que sea queda una intención sin mandar y nadie
   ha programado el envío (una carrera entre añadir y quitar muy seguidos lo
   dejaba colgado indefinidamente, medido: 18 s sin moverse), esto lo reengancha. */
setInterval(() => { try { BubuCart._vigila(); } catch (e) {} }, 1500);

// Estado inicial: el DOM ya viene pintado por Liquid y el carrito viene con él dentro
// de la sección de la cesta, así que no hay que pedir nada. Solo si por lo que sea no
// estuviera (una plantilla que no renderice esa sección) se cae a /cart.js.
// El enlace del slime gratis se lee ANTES de derivar el modelo: así, si el cliente llega
// con un Bubu ya en el carrito, el regalo entra en esta misma carga.
Canjes.desdeUrl();
const inicial = BubuCart._delDocumento();
if (inicial) {
  BubuCart._base = inicial;
  Canjes.vigila(inicial);
  BubuCart._deriva();
  BubuCart._contadores();
  // Las casillas del escenario se reparten YA, no en el primer clic. Antes salían de
  // Liquid; ahora las pone este archivo, y si esperara al primer toque la rejilla haría
  // su único salto justo ahí, que es cuando más se nota.
  BubuCart._ajustaHuecos();
  BubuCart._revisa();
} else {
  conBloqueo(() => BubuCart.get()).then(() => {
    BubuCart._contadores();
    BubuCart._ajustaHuecos();
    BubuCart._revisa();
  });
}